/**
 * The global pyramid, streamed: the tile tree opened and released per body, the
 * traversal that mounts, retires and drops tiles each frame, the credit line,
 * cruise ground and the globe underlay, the height probes over the mounted
 * leaves, which meshes a retired one still covers, and the paced build of each
 * tile mesh.
 */
import * as THREE from 'three';

import {
  atlasToBody, createTileAlbedoCache, createTileMesh, meshHeightAtLatLon, smoothGroundLevel,
  TILE_MESH_QUADS,
  type SiteAlbedoRef, type TileAlbedoCache, type TileMeshHandle, type TileReliefField,
} from './tileMesh.ts';
import type { TileReliefSampler } from './tileRelief.ts';
import {
  createTileTree, directionForLatLon, faceUvForDirection, formatTileKey, FRUSTUM_FLOATS, frustumPlanes, parseTileKey,
  tileForLatLon, type LoadedTile, type TileDrawState, type TileHeightRef, type TileTree, type View,
} from './tileTree.ts';
import {
  createTileSource, creditSourcesForTile, deepestGlobalLevel, deepestLevel, extensionFor, treeExtensions,
  type ClaimState, type CreditSource, type CreditTier, type TileSource,
} from './tileSource.ts';
import { createArtificialFill, type ArtificialFillHandle } from './artificialFill.ts';
import { createCoverageFill, type CoverageFillHandle } from './coverageFill.ts';
import { claimedCellsAt } from './claims.ts';
import { createSiteAlbedo, type SiteAlbedoHandle } from './siteAlbedo.ts';
import { DTM_TILE_BASE, dtmUrl } from './dtmBase.ts';
import { escapeHtml } from '../html.ts';
import { bandEdges } from './camera.ts';
import { createTileWorkerPool, type TileWorkerHandle } from './tileWorker.ts';
import type { LayerPrefsHandle } from './panels/layerPrefs';
import { leafHeightAtLatLon, leafMeshHeightAtLatLon } from './plotField.ts';
import type { OrreryBody } from './orrery';
import { deckAltitudeM } from '../world/bodies.ts';
import type { WorldVec } from '../world/frame';
import type { AtlasLive, AtlasState } from '../atlas';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';
import type { FarFieldHandle } from './farField.ts';
import type { FocusGroundHandle } from './focusGround.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';
import type { HexViewHandle } from './hexView.ts';
import type { InjectsHandle } from './injects.ts';
import type { MountHandle } from './mount.ts';
import type { BuildPanelHandle } from './panels/build.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import type { WorldHostState } from './worldHost.ts';
import type { ZoomLadderHandle } from './zoomLadder.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';
import { albedoTransferOf } from './albedoTransfer.ts';

type CoverProbe = {
  w: number; h: number; rt: THREE.WebGLRenderTarget; key: THREE.MeshBasicMaterial;
  px: Uint8Array; disc: Uint8Array; through: Uint8Array; seen: Uint8Array; queue: Int32Array;
};

// Display order and the tier's own word for the pyramid credit line. The
// PRODUCT after each word is the drawn tile's own (`PyramidSource.product`),
// because these three lines used to be Mars literals and every other baked
// body then read Viking and HiRISE over LOLA and LROC. The instrument and the
// rights statement ride along on the link's title, and the link itself is the
// source's own page.
const CREDIT_ORDER: readonly CreditTier[] = ['terrain', 'imagery', 'detail'];
export const CREDIT_WORD: Readonly<Record<CreditTier, string>> = {
  terrain: 'Terrain',
  imagery: 'Imagery',
  detail: 'Detail',
};

/**
 * How much of a product name one credit phrase carries.
 *
 * The box is 230 px at 9 px ui-monospace, about 42 characters, and a phrase
 * never wraps (see `#atlas-pyramid-credit a` below, and the span beside it), so
 * the tier word and the full stop come off that. Products run to 62 characters ("Voyager 1/2 ISS +
 * Galileo SSI global mosaic, 500 m (54.53 ppd)"), and the whole of one is on
 * the link's title.
 */
const CREDIT_PRODUCT_CHARS = 30;

/** A product short enough for one credit phrase: the part before its first
 *  comma - the posting clause is the tail on every product that has one - cut
 *  at a word boundary if that is still too long. */
export function shortProduct(product: string): string {
  const head = (product.split(',')[0] ?? product).trim();
  if (head.length <= CREDIT_PRODUCT_CHARS) return head;
  const cut = head.slice(0, CREDIT_PRODUCT_CHARS);
  const space = cut.lastIndexOf(' ');
  return `${(space > 12 ? cut.slice(0, space) : cut).trimEnd()}\u2026`;
}

/**
 * How far behind its own datum the underlay globe writes depth, in radii.
 *
 * The globe beneath the pyramid has to LOSE to a tile drawn under the datum -
 * Hellas is 8 km under Mars's sphere, Stickney 2 km under Phobos's - and it
 * used to buy that by writing no depth at all, which handed the whole disc to
 * everything drawn after it: at 12 km over Phobos the Mars globe, 9,400 km
 * away, painted over the moon in front of it wherever a tile had not landed
 * yet, and so did the orbit lines. Depth pushed back rather than turned off
 * keeps the datum's own silhouette, so there is no rim a farther body can show
 * through, and still loses to every tile above the pushed sphere. A quarter of
 * the radius clears the deepest relief a registry body's shape reaches under
 * its own datum (Phobos's 0.18) and stays well inside the distance to anything
 * orbiting a body with a pyramid baked (Phobos itself is 2.76 Mars radii out),
 * so nothing legitimately behind the limb goes with it.
 */
export const GLOBE_DEPTH_PUSH_RADII = 0.25;

/**
 * Every globe but the one the air belongs to draws before the shell.
 *
 * The shell is a screen quad that tests depth and writes none
 * (`SKY_RENDER_ORDER`), so a globe it has to blend over has to be in the depth
 * buffer before it draws. Under the old order the shell went first and Saturn,
 * 1.2 million km past Titan, painted over the haze in front of it. The air's
 * OWN body keeps `AIR_BODY_ORDER` and draws after the quad: its ground carries
 * the same air as an aerial term already.
 */
export const BEHIND_AIR_ORDER = -50;
const AIR_BODY_ORDER = 0;
/** The underlay globe, which is the air body's own and stays after the shell. */
const UNDERLAY_ORDER = -1;

/**
 * Whether a fresh profile draws artificial imagery over ground nobody imaged.
 *
 * One constant rather than a default repeated at each reader: whether an
 * invented picture is what a person sees first is a judgement, not a detail,
 * and flipping it is one line here. A profile that has already touched the
 * ARTIFICIAL FILL row keeps its own answer.
 */
export const ARTIFICIAL_FILL_DEFAULT = true;

/** The level a smooth-ground body's tree reaches while its procedural relief is drawn. */
export const TILE_RELIEF_LEVEL = 12;

export interface PyramidViewDeps {
  readonly mount: MountHandle;
  readonly frameGovernor: FrameGovernorHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly focusGround: FocusGroundHandle;
  readonly focusRig: FocusRigHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly bodyScene: BodySceneHandle;
  readonly injects: InjectsHandle;
  readonly worldHostState: WorldHostState;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands these over through getters. */
  readonly hexViewSeam: HexViewHandle;
  readonly dojoPaints: DojoPaintsHandle;
  readonly ladder: ZoomLadderHandle;
  readonly buildPanel: BuildPanelHandle;
  readonly cameraRig: CameraRigHandle;
  readonly farField: FarFieldHandle;
  readonly _q: THREE.Quaternion;
  readonly _v: THREE.Vector3;
  own(fn: () => void): void;
  readonly renderer: THREE.WebGLRenderer;
  readonly root: HTMLElement;
  readonly state: AtlasState;
  readonly tileGroup: THREE.Group;
  readonly tileWorldPos: WorldVec;
  urlFlag(name: string): boolean;
  readonly globeMaterial: { readonly claimStaleDebug: boolean };
  readonly layerPrefs: LayerPrefsHandle;
}

export interface PyramidViewHandle {
  tileHeightAt(latDeg: number, lonDeg: number): number | null;
  tileMeshHeightAt(latDeg: number, lonDeg: number): number | null;
  tileHeightProbe(latDeg: number, lonDeg: number, mesh: boolean): { h: number; level: number; id: string } | null;
  tileAlbedoAt(latDeg: number, lonDeg: number): { from: LoadedTile; s: number; t: number } | null;
  groundRadiusUnder(id: string, p: WorldVec): number | null;
  /** How far along a ray the relief-displaced ground is met; undefined while none is displaced. */
  drawnGroundHit(origin: WorldVec, dir: WorldVec, far: number): number | null | undefined;
  updateGroundGlide(dt: number): void;
  /** Close up to `m` metres of the held correction at once, for an eye already going down that far. */
  spendGroundGlide(m: number): void;
  readonly groundGlideM: number;
  coverProbe(w: number, h: number): CoverProbe;
  enclosedKeyed(probe: CoverProbe, w: number, h: number): number;
  resolveTileCover(): void;
  dropTileMesh(id: string): void;
  tileWorker(): TileWorkerHandle;
  disposeTileWorker(): void;
  tileBuildUnbudgeted: boolean;
  tileBuildSliceOff: boolean;
  readonly tileReleaseFrameWorst: number;
  readonly tileReleaseWaiting: number;
  readonly tileBuildCostMs: number;
  readonly tileBuildFrameWorstMs: number;
  readonly tileBuildFrameWorstBuilt: number;
  readonly tileBuildFrames: number;
  readonly tileBuildTotal: number;
  readonly tileBuildDeferred: number;
  tileWarmOff: boolean;
  readonly tileWarmedTotal: number;
  readonly tileMountedThisFrame: number;
  readonly tileDrawChanged: boolean;
  readonly TILE_BUILD_SLICE_MS: 4;
  readonly TILE_MOUNTS_PER_FRAME: 2;
  readonly RELEASE_PER_FRAME: 8;
  readonly tileStreamAltM: () => number;
  tileHeightVersion: number;
  readonly tileSource: TileSource | null;
  readonly tileTree: TileTree | null;
  readonly tileMaxLevel: number;
  readonly tileGlobalFloor: number;
  readonly tileError: string | null;
  readonly tileTriangles: number;
  globeUnderlay: boolean;
  underlayMesh: THREE.Mesh | null;
  readonly globeDepthPushM: { value: number };
  injectGlobeDepthPush(shader: { uniforms: Record<string, THREE.IUniform>; vertexShader: string }): void;
  readonly occlusionDebug: { legacyUnderlay: boolean; pyramidHidden: boolean; parentAirDown: boolean };
  readonly tileBody: string | null;
  readonly farFieldHeld: boolean;
  readonly tileCoverage: CoverageFillHandle;
  readonly tileArtificial: ArtificialFillHandle;
  readonly artificialLayerOn: () => boolean;
  /** The procedural relief's shared switch, 1 while the ARTIFICIAL FILL row is on. */
  readonly tileReliefOn: { value: number };
  /** p95 of the last ~200 tile builds' relief time on the main thread, ms; 0 before any. */
  readonly reliefEvalP95Ms: number;
  /** How many tile builds `reliefEvalP95Ms` is over. */
  readonly reliefEvalSamples: number;
  /** Every relief-evaluating tile build since the view opened, so a reader can
   *  tell when the ring holds only builds made after some moment. */
  readonly reliefEvalBuilds: number;
  /** The same p95, sample count and running total for the heights a worker evaluated. */
  readonly reliefWorkerP95Ms: number;
  readonly reliefWorkerSamples: number;
  readonly reliefWorkerEvals: number;
  /** Forgets every worker-evaluated height; the next build of each tile asks again. */
  dropReliefHeights(): void;
  /** `--inject relief-main-thread`: evaluate the heights on the main thread, as before the worker took them. */
  reliefMainThread: boolean;
  unmeasuredFill: string | null;
  readonly tileMeshes: Map<string, TileMeshHandle>;
  /** A reopened pyramid's predecessor, drawn until the new tree settles. */
  readonly tileHeld: ReadonlyMap<string, TileMeshHandle>;
  readonly tilePending: Map<string, LoadedTile>;
  readonly tileRemesh: Map<string, LoadedTile>;
  tileRemeshAll: boolean;
  readonly tileDraw: ReadonlyMap<string, TileDrawState>;
  readonly tileDrawn: Map<string, LoadedTile>;
  readonly tileRetired: Set<string>;
  readonly tileHidden: Set<string>;
  creditMixKey: string | null;
  readonly creditSourceIds: readonly CreditTier[];
  readonly creditText: string;
  creditForceHidden: boolean;
  creditForceBlank: boolean;
  readonly tileTextures: TileAlbedoCache;
  readonly siteAlbedo: SiteAlbedoHandle | null;
  readonly tileDebug: {
    skirts: boolean; geomorph: boolean; splitPx: number; holdSplits: boolean; wholeRelease: boolean;
    orthoTint: boolean; siteImage: boolean; injectStepM: number; injectTiles: Set<string>; injectEdges: number;
    heightFallback: boolean; legacyHorizon: boolean; coverageFill: boolean; legacyTileGate: boolean;
  };
  readonly tileWork: {
    mounted: number; built: number; retargeted: number; dropped: number; evicted: number; remeshed: number;
    uploads: number; traversals: number; buildMs: number; traverseMs: number; coverMs: number; probes: number;
    probeMs: number; progsIn: number; geosIn: number; texsIn: number; warmMs: number; warmed: number;
  };
  readonly tileWorst: {
    dtMs: number; altM: number; mounted: number; built: number; retargeted: number;
    dropped: number; evicted: number; remeshed: number; uploads: number; traversals: number;
    buildMs: number; traverseMs: number; coverMs: number; probes: number; probeMs: number;
    progsIn: number; geosIn: number; texsIn: number; warmMs: number; warmed: number;
    tickMs: number; renderMs: number;
  }[];
  readonly tileHeightMounts: number;
  lastTickMs: number;
  lastRenderMs: number;
  syncCruiseGround(): void;
  tilesActive(): boolean;
  restoreUnderlay(mesh: THREE.Mesh): void;
  applyBodyDrawOrder(): void;
  releaseTiles(): void;
  setArtificialLayer(on: boolean): void;
  tileView(node: OrreryBody): View;
  siteLeafKeys(siteId?: string): Set<string>;
  applyCreditVisibility(): void;
  updateCredit(): void;
  updateTiles(dt: number): void;
  remeshTiles(only?: ReadonlySet<string>): void;
}

export function createPyramidView(deps: PyramidViewDeps): PyramidViewHandle {
  const { _q, _v, own, renderer, root, state, tileGroup, tileWorldPos, urlFlag } = deps;
  const { camera, cameraWorld, atlasRoot } = deps.mount;
  const _rayO = new THREE.Vector3();
  const _rayD = new THREE.Vector3();
  const _rayQ = new THREE.Quaternion();
  const { markLoad, basePixelRatio } = deps.frameGovernor;
  const { aerialDebug } = deps.atmosphereView;
  const { groundDatumM, leafDatumM } = deps.surfaceRadius;
  const {
    bodyMesh, rowById, registryDatumM, reliefAboveDatumM, descendable, imageryFloorDebug, artificialDebug,
    smoothGround,
  } = deps.bodyReaders;
  const { GROUND_GLIDE_M_PER_S } = deps.focusGround;
  const { focusCam, altitude } = deps.focusRig;
  const { ringUnbudgeted, ringHole, ringChurn, descentQueue } = deps.descentLoad;
  const { LOW_FLIGHT_OLD, CRUISE_DATUM_GATE } = deps.injects.state;
  const { solar } = deps.solarScrub;

  // A small pool of decode workers for the life of the tier, built on the
  // first plot that needs it. It holds no state between calls, so a second
  // would only cost threads; closing the Atlas takes them all with it. The
  // pool replaces a dead worker in place, so the handle itself stays live
  // across a single thread's error the way it always could.
  let tileWorkerHandle: TileWorkerHandle | null = null;
  function tileWorker(): TileWorkerHandle {
    tileWorkerHandle ??= createTileWorkerPool();
    return tileWorkerHandle;
  }
  function disposeTileWorker(): void {
    tileWorkerHandle?.dispose();
    tileWorkerHandle = null;
  }

  /**
   * Where streaming starts: the ORBIT floor, 5,478 km on Mars.
   *
   * The globe wears a 2,048 px map, which is 10.4 km per pixel at the equator
   * and exactly the pyramid's own L1. At and below this altitude the traversal
   * first wants L2 (5.2 km) or finer, the first level that beats it; above it
   * every tile the tree would fetch is coarser than what the globe already
   * draws, so nothing is asked for at all.
   */
  const tileStreamAltM = (): number => {
    const edge = bandEdges(registryDatumM(state.bodyId)).ORBIT;
    // Under an opaque deck the ground is not what is seen (docs/roadmap/
    // 05-atmosphere-and-light.md, "Scattering"), so the pyramid does not stream
    // through one either: above the deck the globe IS the cloud top, and a tile
    // of radar backscatter drawn behind 22 km of tau-30 cloud is bandwidth the
    // frame cannot show.
    const row = rowById(state.bodyId);
    const deck = row ? deckAltitudeM(row) : null;
    return deck === null ? edge : Math.min(edge, deck);
  };
  /** One traversal per 100 ms: `boundsOf` allocates per tile, and the LOD set
   *  changes far more slowly than a frame does. */
  const TILE_REQUEST_S = 0.1;
  /**
   * Meshes built per frame.
   *
   * Twenty-four tiles can land in one frame at the tree's in-flight cap, and
   * each is a fraction of a millisecond of trigonometry - together that is a
   * stall on the leg being measured. A whole level or nothing is the TREE's
   * guarantee and says nothing about the meshes: what makes the coarser picture
   * hold while the build is spread is `tileRetired` below, which keeps an
   * unmounted mesh on screen until the meshes replacing it are all there.
   */
  const TILE_MOUNTS_PER_FRAME = 2;
  /** The longest a reopen keeps its predecessor's meshes up, whether or not the new set settled. */
  const TILE_HOLD_MAX_MS = 8000;
  const TILE_HOLD_OVERLAP_FRAMES = 3;
  /**
   * The wall clock one frame gives those builds.
   *
   * A count is a guess at a cost, and the one above was picked against the
   * cheap end of the range: two L12 leaves carrying their own height texture
   * are not two global MOLA tiles. This is the cost itself. A mesh is mounted
   * whole or not at all, so the gate is asked BEFORE a build rather than
   * inside one, and a build that does not fit what is left of the slice waits
   * for the next frame in the order the queue put it in. 4 ms is half of
   * `PLOT_BUILD_SLICE_MS` because the plot build stages itself across these
   * same descent frames and the app's own p50 there is 13.6 ms of a 16.7 ms
   * frame; the 50 ms a player reads as a hitch is the bar this stays clear of,
   * not the budget to spend up to.
   */
  const TILE_BUILD_SLICE_MS = 4;
  /**
   * Bumped every time the ground a height probe would read changes: a tile
   * mounting or unmounting, a MESH being built or rebuilt, or a cover change
   * moving which mesh answers under a point.
   *
   * `tileHeightProbe` reads whatever is drawn right now, so a vertex the hex
   * overlay draped at one revision reads a different height once a finer tile
   * streams in under it. The overlay compares this against the version it last
   * built from and rebuilds, rather than trusting a pose-only "did the camera
   * move" check to also mean "is the ground the same ground". The mesh sites
   * matter as much as the traversal: mounts are budgeted per frame, so the
   * traversal's own bump lands frames before the ground it promised.
   */
  let tileHeightVersion = 0;

  let tileSource: TileSource | null = null;
  let tileTree: TileTree | null = null;
  let tileInit: Promise<void> | null = null;
  let tileMaxLevel = 0;
  /** The deepest level the GLOBAL product reaches; below it a tile is a site leaf. */
  let tileGlobalFloor = 0;
  let tileError: string | null = null;
  let tileTriangles = 0;
  let globeUnderlay = false;
  /** Which globe the underlay was applied TO, so a focus change hands it back. */
  let underlayMesh: THREE.Mesh | null = null;
  /**
   * How far behind its own datum the underlay globe writes depth, scene metres.
   *
   * One uniform shared by every globe carrying the chunk; 0 whenever no globe
   * is an underlay, which is what a body that never streams a tile reads.
   */
  const globeDepthPushM = { value: 0 };
  /**
   * The underlay's depth push, into a shader the caller is already building.
   *
   * Shared by the focused globe's own program and by `tileCoverage`'s key
   * material: that probe swaps the globe's material out for a flat key, and a
   * key without this draws the globe at its true depth. On a displaced globe,
   * whose surface IS the ground, that puts it through the leaves in places and
   * the coverage reading counts the probe's own missing push as ground the
   * traversal never asked for.
   */
  function injectGlobeDepthPush(shader: {
    uniforms: Record<string, THREE.IUniform>; vertexShader: string;
  }): void {
    shader.uniforms['uGlobeDepthPush'] = globeDepthPushM;
    shader.vertexShader = `uniform float uGlobeDepthPush;\n${shader.vertexShader}`
      .replace('#include <logdepthbuf_vertex>',
        '#include <logdepthbuf_vertex>\n#ifdef USE_LOGDEPTHBUF\n'
        + '\tvFragDepth += uGlobeDepthPush;\n#endif');
  }
  /** `--occlusion`'s knobs: the pre-fix draw, the pyramid out of frame, and the
   *  parent's air held down for the pass that keys its own globe. */
  const occlusionDebug = { legacyUnderlay: false, pyramidHidden: false, parentAirDown: false };
  let tileAccum = TILE_REQUEST_S;
  /** Which body the open tree belongs to, so leaving one tears it down. */
  let tileBody: string | null = null;
  /**
   * `farFieldWanted()` drops the instant ascent leaves SURFACE, and `orreryOn`
   * does not pick the ground back up for ~0.2 s while `descent` eases past its
   * smoothstep midpoint - a grey frame with nothing ground-related drawn.
   * Latched true while wanted, held through that gap, and released once the
   * orrery is back so a later loss of `wanted` (leaving PLOT for real) is not
   * masked. `--inject legacy-fade` (`legacyPlotFade`) forces it off to put
   * back the pre-fix gate `surface-clamp --ascent`'s control validates
   * against.
   */
  let farFieldHeld = false;
  /**
   * What an unmeasured imagery texel draws on the streamed tiles.
   *
   * One handle for the whole session, retargeted at the body whose pyramid is
   * open, so a material built for the last body cannot go on filling with its
   * colour. The globes carry their own (`orrery.ts`): every body's disc is on
   * screen at SYSTEM and each fills with its own row's colour.
   */
  const tileCoverage = createCoverageFill(
    rowById(state.bodyId)?.albedo_color ?? '#808080');
  /**
   * The ARTIFICIAL FILL switch as the streamed tiles read it.
   *
   * One handle for the session beside `tileCoverage`, and the switch only: a
   * tile's fill is its own texture out of the tile cache, so what is shared
   * here is `on` and the `art-over-measured` control. Every material this
   * feeds is a `-art1` program, so a body with no fill links nothing new.
   */
  const tileArtificial = createArtificialFill(true);
  /** Whether the tile band may request `.art.webp` at all: the layer, as stored. */
  const artificialLayerOn = (): boolean => deps.layerPrefs.get('artificial') ?? ARTIFICIAL_FILL_DEFAULT;
  // Set by `setArtificialLayer`, which the orrery's landing calls with the stored row.
  const tileReliefOn = { value: 0 };
  /** The body's procedural relief when the row is on and its class maps have landed. */
  function tileReliefFor(body: string | null): TileReliefSampler | null {
    if (body === null || !artificialLayerOn()) return null;
    return deps.bodyScene.state.orrery?.byId(body)?.tileRelief ?? null;
  }
  /** Whether the open tree was cut to the relief's depth, and the sampler version it last saw. */
  let tileReliefDeep = false;
  let tileReliefSeen = -1;
  /** The last 200 timings, their p95, and how many there have been in all. */
  function msRing(): { note(ms: number): void; p95(): number; readonly samples: number; readonly total: number } {
    const ring: number[] = [];
    let next = 0;
    let total = 0;
    return {
      note(ms: number): void {
        total++;
        if (ring.length < 200) ring.push(ms);
        else ring[next] = ms;
        next = (next + 1) % 200;
      },
      p95(): number {
        if (ring.length === 0) return 0;
        const sorted = [...ring].sort((a, b) => a - b);
        return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] ?? 0;
      },
      get samples() { return ring.length; },
      get total() { return total; },
    };
  }
  const reliefEvalTimes = msRing();
  const reliefWorkerTimes = msRing();
  /**
   * Heights a worker evaluated, by tile id, for the current `reliefGen`; null
   * where the worker failed and the build evaluates on the main thread.
   */
  const reliefHeights = new Map<string, Float64Array | null>();
  const reliefAsked = new Set<string>();
  let reliefGen = 0;
  let reliefMainThread = false;
  /** Enough to keep two relief workers busy without queueing imagery decodes behind a whole band. */
  const RELIEF_IN_FLIGHT = 6;
  function dropReliefHeights(): void {
    reliefGen++;
    reliefHeights.clear();
    reliefAsked.clear();
  }
  function forgetReliefHeights(id: string): void {
    reliefHeights.delete(id);
    reliefAsked.delete(id);
  }
  /** The colour `--inject nodata-black` forces, or null for each row's own. */
  let unmeasuredFill: string | null = null;
  const tileMeshes = new Map<string, TileMeshHandle>();
  /**
   * A reopened pyramid's predecessor, kept drawn in `tileGroup` until the new
   * tree settles, so a reopen never shows a frame with no ground.
   */
  const tileHeld = new Map<string, TileMeshHandle>();
  let tileHeldBody: string | null = null;
  let tileHeldSince = 0;
  let tileHeldMaxLevel = 0;
  /** Frames since the new set went visible over the held one, 0 while it is hidden. */
  let tileHeldOverlap = 0;
  /** Traversals in a row that mounted and unmounted nothing with every wanted tile resident. */
  let tileQuietPasses = 0;
  /** Mounted by the tree, not yet meshed: the per-frame build budget's queue. */
  const tilePending = new Map<string, LoadedTile>();
  /**
   * Drawn, and to be rebuilt: the SWAP queue.
   *
   * A rebuild keeps its old mesh until the replacement exists, so a whole-set
   * remesh - the relief slider, a skirt toggle - is 225 frames of stale ground
   * rather than 225 frames of no ground at two builds a frame.
   */
  const tileRemesh = new Map<string, LoadedTile>();
  /** A whole-set rebuild asked for since the last frame; the latest value wins. */
  let tileRemeshAll = false;
  /** The last traversal's per-tile albedo and morph, read when a mesh is built. */
  let tileDraw: ReadonlyMap<string, TileDrawState> = new Map();
  /** The tiles behind the meshes, so a skirt or geomorph toggle can rebuild them. */
  const tileDrawn = new Map<string, LoadedTile>();
  /**
   * A level change on screen, held until the whole replacement is meshed.
   *
   * The tree's whole-level-or-nothing guarantee is about what is RESIDENT; the
   * mesh set is built two a frame. Dropping a parent's mesh in the traversal
   * frame while its four children queued behind that budget uncovered a quarter
   * of its ground to the globe underlay for a dozen frames, per split, all the
   * way down - which is the flashing in the user's report
   * (`tools/preview/tile-swap-check.mjs`). So an unmounted mesh is RETIRED
   * rather than dropped: it keeps drawing its ground until the meshes that
   * replace it are all there. A mesh whose ground a retired one still covers is
   * kept INVISIBLE meanwhile, so no two levels overdraw each other, which
   * coplanar parent and child geometry would z-fight over.
   *
   * Both directions: a split retires the parent until its descendants land, a
   * merge retires the four children until the ancestor does.
   */
  const tileRetired = new Set<string>();
  /** Drawn meshes not yet revealed, because a retired mesh still covers them. */
  const tileHidden = new Set<string>();
  /**
   * Meshes one frame may uncover, past the first cover it drops.
   *
   * A held split used to wait for every tile under it, and a descent splits
   * that ground faster than two builds a frame land it, so the cover went only
   * once the queue drained and uncovered the whole subtree in one frame. A
   * hidden tile the descent has passed is now kept as a step (`releaseInSteps`),
   * so a cover goes as soon as the next level under it is meshed.
   */
  const RELEASE_PER_FRAME = 8;
  /**
   * Tiles the descent passed before their mesh was built, still queued in
   * `tilePending` as a step of the cover over them, with the draw state they
   * last had. A descent splits a tile before two builds a frame reach it, and
   * without the step the cover's next level down is the whole leaf set.
   */
  const tileStepDraw = new Map<string, TileDrawState>();
  let tileReleasedThisFrame = 0;
  /** Covers ready on the last pass that the per-frame cap held back. */
  let tileReleaseWaiting = 0;
  /** The most meshes one frame has uncovered since the Atlas opened. */
  let tileReleaseFrameWorst = 0;
  /** A cover went or waited, so the steps under it are read again next frame. */
  let tileCoverDirty = false;
  /**
   * The USGS/NASA credit line: which product tiers are behind the drawn set,
   * and the text and markup built from them.
   *
   * `creditMixKey` is the tiers-plus-urls actually rendered last time, so a
   * traversal that changes nothing about the mix costs a `Map` build and a
   * string compare rather than an `innerHTML` write every 100 ms.
   */
  let creditMixKey: string | null = null;
  let creditSourceIds: readonly CreditTier[] = [];
  let creditText = '';
  let creditVisible = false;
  /** `layout-check --self`: fakes the failure the assertion exists to catch. */
  let creditForceHidden = false;
  /** `layout-check --self`: fakes an empty product mix with tiles still drawn. */
  let creditForceBlank = false;
  /** One GL texture per albedo tile, shared by every descendant borrowing it. */
  const tileTextures: TileAlbedoCache = createTileAlbedoCache();
  own(() => tileTextures.dispose());
  /** The region orthoimage the leaves under a site draw with. */
  let siteAlbedo: SiteAlbedoHandle | null = null;
  /**
   * Skirts and the geomorph, switchable.
   *
   * The seam-check's calibration arm has to see the crack these close, so it
   * turns both off and requires the seam band to read far above the background
   * before it trusts the gated arm at all (docs/VERIFICATION.md).
   */
  const tileDebug = {
    skirts: true, geomorph: true, splitPx: 0,
    /**
     * Hold a level change on screen until the whole replacement is meshed.
     *
     * `tile-swap-check.mjs --self` turns it off, which is the pre-fix
     * behaviour: the outgoing mesh goes in the traversal frame and the ground
     * falls back to the globe underlay until the build budget catches up.
     */
    holdSplits: true,
    /**
     * Release a held split the pre-fix way: drop every cover whose whole
     * subtree is meshed in one pass, with no cap and no kept steps.
     * `descent-dojo --ship --inject whole-release` is the control. DEV only,
     * so no build carries it.
     */
    wholeRelease: TEST_SWITCHES && urlFlag('wholerelease'),
    /**
     * The site orthoimage is luminance detail over the inherited colour.
     *
     * Off, it is the sole map again - a greyscale product replacing a colour
     * one, which is what made two leaves at the same level disagree. The colour
     * arm of `tile-swap-check.mjs` uses it as its self arm.
     */
    orthoTint: true,
    /**
     * Use the region orthoimage over a leaf at all.
     *
     * Off, a leaf inherits the L6 Viking albedo like any other tile below the
     * floor. The colour arm reads the SAME ground both ways and compares, which
     * is the only way to isolate the product from the terrain under it.
     */
    siteImage: true,
    /** Metres the edge ring of `injectTiles` is lifted by. */
    injectStepM: 0,
    /**
     * Which tiles carry the lip: named, not a whole level.
     *
     * A whole level lips EVERY edge on it, and at 16 km the tile grid is denser
     * than the 60 to 250 px the background band is chosen from - so the
     * background lands on another lipped edge and reads higher than the seam.
     * One tile keeps the comparison band on clean ground.
     */
    injectTiles: new Set<string>(),
    /** Which of the injected tiles' edges carry the lip; 0 is all four. */
    injectEdges: 0,
    /**
     * Let a leaf under the height floor take its relief from an ancestor.
     *
     * Off, every such leaf is drawn on the datum sphere again - the flat ground
     * Mercury, Venus and Phobos used to draw below their height floor, and
     * `surface-clamp --relief --inject flat-floor`'s own control.
     */
    heightFallback: true,
    /**
     * The visibility rule before a body that is not round was streamed.
     *
     * The camera radius off the focus altitude over the datum alone, and relief
     * at one percent of the radius whatever shape the body is - which on Phobos
     * is 111 m against a 1,900 m bulge and left a third of the disc's radius
     * unasked for. `tile-swap-check --coverage --inject legacy-horizon`.
     */
    legacyHorizon: false,
    /**
     * Sample the albedo's own coverage on a streamed tile at all.
     *
     * Off, a tile material is built with no coverage ref, so `coverageFill.ts`'s
     * chunk is not in its program and an unmeasured texel draws whatever the
     * bake left in its RGB whatever the fill is set to. The globe keeps its own,
     * so the two paths can be read apart: `--coverage --inject fill-tiles-none`.
     */
    coverageFill: true,
    /**
     * The streaming gate before it asked about one body.
     *
     * On, the altitude to whatever the focus camera is on is compared with the
     * ladder body's own edge again, whichever bodies those two are.
     * `descent-dojo --shipmark --inject legacy-tile-gate`.
     */
    legacyTileGate: false,
  };

  const _tileEye = new THREE.Vector3();
  const _tileQ = new THREE.Quaternion();
  const _tileCamQ = new THREE.Quaternion();
  const _tileGroupQ = new THREE.Quaternion();
  const _tileRootQ = new THREE.Quaternion();
  const _tileFwd = new THREE.Vector3();
  const _tileUp = new THREE.Vector3();
  /** The traversal's own frustum, rewritten in place every request. */
  const _tilePlanes: number[] = new Array<number>(FRUSTUM_FLOATS).fill(0);
  const _tileFwdBody: [number, number, number] = [0, 0, 0];
  const _tileUpBody: [number, number, number] = [0, 0, 0];

  /**
   * What the pyramid did on one frame, and the frames that cost the most.
   *
   * A leg's max frame time is one frame and the totals in `tiles` cannot say
   * which; this keeps the frames themselves so a 50 ms max can be attributed
   * to mounts, mesh builds, albedo uploads or a retarget rather than guessed
   * at. Read and cleared per leg by `descent-dojo`.
   */
  interface TileFrameWork {
    dtMs: number;
    altM: number;
    mounted: number;
    built: number;
    retargeted: number;
    dropped: number;
    evicted: number;
    remeshed: number;
    uploads: number;
    traversals: number;
    buildMs: number;
    /** The traversal pass and the cover pass: the two walks over the draw set. */
    traverseMs: number;
    coverMs: number;
    /** Height probes taken off the tick, by the camera's own clearance solve. */
    probes: number;
    probeMs: number;
    /** What the render itself brought in: a decode, an upload or a link. */
    progsIn: number;
    geosIn: number;
    texsIn: number;
    /** Textures uploaded off the reveal by `warmTileMesh`, and what it cost. */
    warmMs: number;
    warmed: number;
    /** The whole atlas tick, and the render inside it: JS we own, or not. */
    tickMs: number;
    renderMs: number;
  }
  const TILE_WORST_KEEP = 12;
  /** A gated leg's own max budget: over this a frame is kept whatever it did. */
  const TILE_SLOW_FRAME_MS = 50;
  const tileWork = {
    mounted: 0, built: 0, retargeted: 0, dropped: 0,
    evicted: 0, remeshed: 0, uploads: 0, traversals: 0, buildMs: 0,
    traverseMs: 0, coverMs: 0, probes: 0, probeMs: 0,
    progsIn: 0, geosIn: 0, texsIn: 0, warmMs: 0, warmed: 0,
  };
  const tileWorst: TileFrameWork[] = [];
  /** Tiles mounted carrying elevation since the body's pyramid opened. */
  let tileHeightMounts = 0;
  let tileUploadsSeen = 0;
  /** The previous tick's own cost, since a frame's record closes on the next one. */
  let lastTickMs = 0;
  let lastRenderMs = 0;

  /**
   * Close the previous frame's record against THIS frame's delta.
   *
   * `dt` is the time since the last frame, so the work done on frame N shows
   * up in frame N+1's sample - which is the same reason the governor marks
   * `dirtyNext` rather than `frameDirty`.
   */
  function noteTileFrame(dtMs: number): void {
    const w = tileWork;
    const uploads = tileTextures.created - tileUploadsSeen;
    tileUploadsSeen = tileTextures.created;
    w.uploads += uploads;
    // A frame over the budget is kept whether or not the pyramid did anything
    // in it: a traversal-only pass recorded nothing at all, so a stall inside
    // one read as no pyramid work rather than as work with a cost.
    if (w.mounted || w.built || w.retargeted || w.dropped || w.evicted
      || w.remeshed || w.uploads || w.traversals || dtMs > TILE_SLOW_FRAME_MS) {
      tileWorst.push({
        dtMs: +dtMs.toFixed(2), altM: Math.round(altitude()),
        mounted: w.mounted, built: w.built, retargeted: w.retargeted,
        dropped: w.dropped, evicted: w.evicted, remeshed: w.remeshed,
        uploads: w.uploads, traversals: w.traversals,
        buildMs: +w.buildMs.toFixed(2),
        traverseMs: +w.traverseMs.toFixed(2), coverMs: +w.coverMs.toFixed(2),
        probes: w.probes, probeMs: +w.probeMs.toFixed(2),
        progsIn: w.progsIn, geosIn: w.geosIn, texsIn: w.texsIn,
        warmMs: +w.warmMs.toFixed(2), warmed: w.warmed,
        tickMs: +lastTickMs.toFixed(2), renderMs: +lastRenderMs.toFixed(2),
      });
      tileWorst.sort((a, b) => b.dtMs - a.dtMs);
      if (tileWorst.length > TILE_WORST_KEEP) tileWorst.length = TILE_WORST_KEEP;
    }
    w.mounted = 0; w.built = 0; w.retargeted = 0; w.dropped = 0;
    w.evicted = 0; w.remeshed = 0; w.uploads = 0; w.traversals = 0; w.buildMs = 0;
    w.traverseMs = 0; w.coverMs = 0; w.probes = 0; w.probeMs = 0;
    w.progsIn = 0; w.geosIn = 0; w.texsIn = 0; w.warmMs = 0; w.warmed = 0;
  }

  /**
   * How far the CRUISE camera is over the body cruise draws in full, metres
   * above the datum, or null when there is no such body.
   *
   * `altitude()` is the focus rig's own number and the focus rig is not driven
   * out here, so the ladder's altitude is whatever the last visit left.
   */
  function cruiseAltitudeM(): number | null {
    const node = deps.bodyScene.state.bodyNode;
    if (!deps.worldHostState.externalWorld || node === null || deps.worldHostState.worldBodyId === null) return null;
    const d = Math.hypot(
      cameraWorld.x - node.world.x, cameraWorld.y - node.world.y, cameraWorld.z - node.world.z);
    return d - groundDatumM(deps.worldHostState.worldBodyId);
  }
  const _cruiseSimDir: WorldVec = { x: 0, y: 0, z: 0 };
  /**
   * `cruiseAltitudeM()` or the camera's height over the drawn globe's own facet
   * under it, whichever is lower, on a body drawn as a sphere with relief.
   * Luna's sunward highlands stand about 6 km over its datum, past the whole
   * entry height, so the datum alone never armed the pyramid over them.
   */
  function cruiseGroundHeightM(): number | null {
    const alt = cruiseAltitudeM();
    const id = deps.worldHostState.worldBodyId;
    const node = deps.bodyScene.state.bodyNode;
    if (alt === null || CRUISE_DATUM_GATE || id === null || node === null) return alt;
    const dx = cameraWorld.x - node.world.x;
    const dy = cameraWorld.y - node.world.y;
    const dz = cameraWorld.z - node.world.z;
    // Scene axes back to the sim's ecliptic ones, which the reader takes.
    _cruiseSimDir.x = dx; _cruiseSimDir.y = -dz; _cruiseSimDir.z = dy;
    const r = deps.surfaceRadius.reliefSurfaceRadiusM(id, _cruiseSimDir);
    return r === null ? alt : Math.min(alt, Math.hypot(dx, dy, dz) - r);
  }
  /**
   * Fraction of the entry height the pyramid is held to on the way back up.
   *
   * Hysteresis rather than one edge: a ship holding station on the edge would
   * otherwise arm and release the traversal frame by frame.
   */
  const CRUISE_GROUND_RELEASE = 1.1;
  /**
   * `tileStreamAltM()` is the ladder pane's own streaming floor, sized off the
   * globe's texel against the PANE's zoom - 5,478 km on Mars, 1,065 km on
   * Luna - and the cruise chase camera is a much narrower view than that pane
   * ever is. Parked just inside the Mars edge cruise asked for 123 tile
   * requests and 12.7 MB before this cap, thousands of kilometres out from
   * where the fault this streams for ever shows: the issue is a black frame
   * under about 4 km. Cruise never arms past this even where the pane's own
   * floor is higher.
   */
  const CRUISE_GROUND_CEILING_M = 4000;
  /** Texels across the globe map every body is painted with, bake_pyramid.py's
   *  fixed export size regardless of the body's own radius. */
  const GLOBE_MAP_TEXELS = 2048;
  /** Metres of ground one globe texel covers at this body's equator. */
  function globeTexelM(id: string | null): number {
    return (2 * Math.PI * groundDatumM(id)) / GLOBE_MAP_TEXELS;
  }
  /**
   * Whether cruise draws the pyramid under the ship this frame.
   *
   * Under the lesser of `tileStreamAltM()` and `CRUISE_GROUND_CEILING_M`,
   * because the first tile level finer than the globe's own 2,048-texel map
   * can be thousands of kilometres out, and the globe map is all cruise draws
   * without this: one of its texels fills the whole frame a few kilometres up
   * (5.1 km on Luna), and the 96x48 sphere it is painted on turns its faces
   * away from an eye that low over a 113 km facet, so an airless body's frame
   * goes black.
   */
  function syncCruiseGround(): void {
    const alt = LOW_FLIGHT_OLD || !descendable() ? null : cruiseGroundHeightM();
    deps.worldHostState.cruiseGroundHeightM = alt;
    if (alt === null) {
      deps.worldHostState.cruiseGroundOn = false;
      return;
    }
    const enter = Math.min(tileStreamAltM(), Math.max(CRUISE_GROUND_CEILING_M, globeTexelM(deps.worldHostState.worldBodyId)));
    deps.worldHostState.cruiseGroundOn = alt <= (deps.worldHostState.cruiseGroundOn ? enter * CRUISE_GROUND_RELEASE : enter);
  }

  function tilesActive(): boolean {
    // The registry's own `availability.descend` is what says a body has a
    // pyramid to stream; a body without one never asks for a tile.
    //
    // The focus camera has to be ON that body before its altitude means
    // anything here: `altitude()` is the distance to whatever is focused and
    // the threshold beside it is the ladder body's, so focused on the ship
    // marker 5 km away the comparison reads 5 km against Mars's orbit edge and
    // arms the whole pyramid from 20,000 km out.
    if (deps.worldHostState.externalWorld) return deps.worldHostState.cruiseGroundOn;
    const onLadderBody = tileDebug.legacyTileGate || focusCam.state.bodyId === state.bodyId;
    return descendable() && onLadderBody && altitude() <= tileStreamAltM();
  }

  /**
   * The globe drops BEHIND its own datum while tiles are drawn over it.
   *
   * The globe is the coarse picture BENEATH the pyramid - what a level that has
   * not arrived yet falls back to - and it is drawn on the datum sphere, which
   * half of Mars is below: Hellas by 8 km, Jezero by 2.5. A globe writing the
   * datum's own depth would bury exactly the tiles that need it most. Drawing
   * it first with its depth pushed `GLOBE_DEPTH_PUSH_RADII` back makes the tile
   * win wherever there is one, leaves the globe showing wherever there is not,
   * and - unlike the depthWrite it replaces - still hides the rest of the solar
   * system behind the body.
   */
  function setGlobeUnderlay(on: boolean): void {
    const mesh = bodyMesh();
    if (!mesh) return;
    // Recomputed whether or not the flag moved: the drawn radius becomes the
    // manifest's datum when the pyramid opens, after the underlay went on.
    const push = on && !occlusionDebug.legacyUnderlay
      ? (deps.bodyScene.state.bodyNode?.drawRadiusM ?? 0) * GLOBE_DEPTH_PUSH_RADII : 0;
    if (globeDepthPushM.value !== push) {
      globeDepthPushM.value = push;
      deps.live.needsRender = true;
    }
    if (globeUnderlay === on && underlayMesh === (on ? mesh : null)) return;
    // The last body's globe goes back to itself even though `bodyMesh()` has
    // already moved on: a focus change used to leave it drawn first and out of
    // the depth buffer for the rest of the session.
    if (underlayMesh && underlayMesh !== mesh) restoreUnderlay(underlayMesh);
    globeUnderlay = on;
    underlayMesh = on ? mesh : null;
    (mesh.material as THREE.Material).depthWrite = !(on && occlusionDebug.legacyUnderlay);
    mesh.renderOrder = on ? UNDERLAY_ORDER : bodyDrawOrder(mesh.name);
    deps.live.needsRender = true;
  }

  /** A globe handed back the depth and the order the underlay took from it. */
  function restoreUnderlay(mesh: THREE.Mesh): void {
    (mesh.material as THREE.Material).depthWrite = true;
    mesh.renderOrder = bodyDrawOrder(mesh.name);
  }

  /** Where one globe draws against the air shell. `BEHIND_AIR_ORDER` says why. */
  function bodyDrawOrder(id: string): number {
    if (aerialDebug.legacySkyOrder) return AIR_BODY_ORDER;
    return id === deps.atmosphereView.atmoBodyId ? AIR_BODY_ORDER : BEHIND_AIR_ORDER;
  }

  /**
   * Put every globe on the right side of the shell, after a mount or a control.
   *
   * The underlay globe is left alone: `setGlobeUnderlay` owns that one, and it
   * is the focused body's, which is the nearest thing in the frame.
   */
  function applyBodyDrawOrder(): void {
    for (const b of deps.bodyScene.state.orrery?.bodies ?? []) {
      if (b.mesh === underlayMesh) continue;
      b.mesh.renderOrder = bodyDrawOrder(b.row.id);
    }
    deps.atmosphereView.atmoSky?.setLegacyOrder(aerialDebug.legacySkyOrder);
    deps.live.needsRender = true;
  }

  /**
   * Every mesh and every queue that feeds one, given back. The tree and its
   * resident tiles stay: that cache is an LRU by bytes with its own budget and
   * is the cache of record for anything the streamer fetched
   * (docs/ATLAS-TERRAIN.md, "Tile tree contract").
   */
  function releaseTileMeshes(): void {
    for (const id of [...tileMeshes.keys()]) dropTileMesh(id);
    tileRetired.clear();
    tileHidden.clear();
    tilePending.clear();
    tileRemesh.clear();
    tileRemeshAll = false;
    tileCoverDirty = false;
    tileReleaseWaiting = 0;
    tileStepDraw.clear();
    tileDrawn.clear();
    tileDraw = new Map();
    tileTriangles = 0;
    dropReliefHeights();
  }

  /** The held set given back, and the new tree's meshes shown as their covers say. */
  function dropHeldTiles(): void {
    if (tileHeld.size === 0) return;
    for (const h of tileHeld.values()) {
      tileGroup.remove(h.object);
      h.dispose();
    }
    tileHeld.clear();
    tileHeldBody = null;
    tileHeldMaxLevel = 0;
    tileHeldOverlap = 0;
    for (const [id, h] of tileMeshes) h.object.visible = !tileHidden.has(id);
    tileHeightVersion++;
    deps.live.needsRender = true;
  }

  /** Whether the open tree has fetched, meshed and revealed everything it wants. */
  function tileSetSettled(): boolean {
    return tileTree !== null && tileMeshes.size > 0 && tileQuietPasses >= 2
      && tilePending.size === 0 && tileRemesh.size === 0 && tileRetired.size === 0;
  }

  /** The mesh drawn over `id`: the open tree's own, or a held one while it builds. */
  function drawnTileMesh(id: string): TileMeshHandle | null {
    const m = tileMeshes.get(id);
    if (m && m.object.visible) return m;
    return tileHeld.get(id) ?? null;
  }

  /** Everything the pyramid holds, given back. Called on a body change and on dispose. */
  function releaseTiles(): void {
    dropHeldTiles();
    tileQuietPasses = 0;
    releaseTileMeshes();
    tileTree?.dispose();
    tileTree = null;
    tileSource?.dispose();
    tileSource = null;
    siteAlbedo?.dispose();
    siteAlbedo = null;
    tileInit = null;
    tileMaxLevel = 0;
    tileGlobalFloor = 0;
    tileReliefDeep = false;
    tileReliefSeen = -1;
    tileHeightMounts = 0;
    // One sphere both ways: `datumRadiusM` falls back to the globe's own
    // radius the moment the manifest goes, which is the registry's on a sphere
    // and the same datum on a shape globe built off this pyramid's L0 tiles.
    if (tileBody !== null) deps.bodyScene.state.orrery?.resetDrawRadius(tileBody);
    tileBody = null;
    tileGroup.visible = false;
    setGlobeUnderlay(false);
  }
  own(releaseTiles);

  /**
   * `releaseTiles` for a reopen on the same body: the meshes on screen stay
   * drawn in `tileHeld` until the tree replacing them has settled.
   */
  function reopenTilesHeld(): void {
    // A reopen inside a hold keeps the held set: the tree it was waiting on is still hidden.
    const body = tileHeld.size > 0 ? tileHeldBody : tileBody;
    const maxLevel = tileHeld.size > 0 ? tileHeldMaxLevel : tileMaxLevel;
    const keep = new Map(tileHeld);
    tileHeld.clear();
    if (keep.size === 0 && body !== null) {
      for (const [id, h] of tileMeshes) if (h.object.visible) keep.set(id, h);
      for (const id of keep.keys()) tileMeshes.delete(id);
    }
    releaseTiles();
    tileBody = body;
    if (keep.size === 0) return;
    for (const [id, h] of keep) tileHeld.set(id, h);
    tileHeldBody = body;
    tileHeldMaxLevel = maxLevel;
    tileHeldOverlap = 0;
    tileHeldSince = performance.now();
    setGlobeUnderlay(true);
  }

  /**
   * The ARTIFICIAL FILL row, on both surfaces that read it.
   *
   * OFF is a uniform and nothing else: `fxArtOn` at 0 is a bit-identical
   * frame, the resident tiles keep the fill they already hold, and not one
   * further `.art.webp` is requested. ON reopens the pyramid, because what
   * changed is the FETCH PLAN and every resident tile was fetched without the
   * role in it - the same reason `setImageryFloorDebug` reopens. Either
   * reopen keeps the ground it had on screen until the new tree has settled.
   */
  function setArtificialLayer(on: boolean): void {
    const was = tileArtificial.ref.on.value > 0;
    deps.bodyScene.state.orrery?.setArtificialFill(on);
    tileArtificial.setOn(on);
    tileReliefOn.value = on ? 1 : 0;
    // OFF also reopens a tree cut to the relief's depth, back to the smooth ground's own.
    if ((on ? !was : tileReliefDeep) && (tileSource !== null || tileInit !== null)) {
      reopenTilesHeld();
      deps.live.cameraDirty = true;
    }
  }

  function ensureTileTree(): void {
    if (tileInit) return;
    const body = state.bodyId;
    if (body === null) return;
    tileInit = (async () => {
      // The source's `ready()` is what says a body's floor levels, so the tree
      // cannot be configured before pyramid.json is in.
      const source = createTileSource({
        body, baseUrl: DTM_TILE_BASE, worker: tileWorker(),
        injectRole: imageryFloorDebug.heightRoleFrom === null
          ? null
          : { role: 'height', from: imageryFloorDebug.heightRoleFrom },
        // `descent-dojo --artificial`'s crossing leg: reopen the pyramid as a
        // deployed bucket that predates the role would serve it.
        dropRole: artificialDebug.dropTileRole ? 'artificial' : null,
        // Read fresh at every decode, not captured once: the resolution the
        // overlay draws at can step between when a tile is FETCHED and when
        // it DECODES, and a tile's claim texture should bake against the rung
        // that was current at ITS OWN decode (see `TileClaims` in tileTree.ts).
        getClaimState: (): ClaimState | null => {
          const res = deps.hexViewSeam.hexOverlay.resolutionAt(deps.hexViewSeam.hexView());
          return res === null ? null : { res, cells: Array.from(claimedCellsAt(res)) };
        },
        // Read per fetch, not captured: the row can move under a live band,
        // and turning it ON reopens the pyramid so the resident set is
        // refetched with the role in its plan.
        wantArtificial: artificialLayerOn,
      });
      const manifest = await source.ready();
      if (deps.live.disposed) { source.dispose(); return; }
      // The DEEPER of the two roles, not the shallower. Mars's height reaches L7
      // and its Viking albedo stops at L6; an L7 tile draws with its L6
      // ancestor's imagery over the quarter of it that this tile covers, which
      // the tree hands over as a `TileDrawState` and pins the ancestor for. On
      // an imagery-only body the albedo floor is the only one there is, and
      // reading height's gave 0, which held the pyramid at its root set.
      // Below the global floor a detail region's own leaves carry on, and
      // `deepestLevel` is the deepest of the three.
      // Smooth ground carries the tree on past every role's floor, with no
      // payload at those levels: the leaves are the datum sphere under their
      // ancestor's imagery, cut fine enough to stand on.
      // Procedural relief needs cells of tens of metres to draw its crests.
      tileReliefDeep = smoothGround(body) && tileReliefFor(body) !== null;
      tileReliefSeen = deps.bodyScene.state.orrery?.byId(body)?.tileReliefVersion ?? -1;
      const groundLevel = smoothGround(body)
        ? Math.max(
          smoothGroundLevel(manifest.datum_radius_m, deps.focusRig.STANDING_EYE_M),
          tileReliefDeep ? TILE_RELIEF_LEVEL : 0,
        )
        : 0;
      tileMaxLevel = Math.max(deepestLevel(manifest), groundLevel);
      tileGlobalFloor = Math.max(deepestGlobalLevel(manifest), groundLevel);
      tileSource = source;
      // The pyramid's own derived fill where it has one - a hole in this
      // body's imagery now reads as more of its own measured ground rather
      // than the registry's flat, and often brighter, stand-in colour. Read
      // off the manifest already in hand, not fetched again.
      const coverageFillHex = manifest.roles.get('albedo')?.unmeasured_rgb
        ?? rowById(body)?.albedo_color ?? '#808080';
      // The fill is cut from the ground's own bytes, so it decodes as they do.
      const groundTransfer = albedoTransferOf(rowById(body));
      tileCoverage.setBody(coverageFillHex, groundTransfer);
      tileArtificial.setTransfer(groundTransfer);
      deps.bodyScene.state.orrery?.setCoverageFill(body, coverageFillHex, groundTransfer);
      // One sphere: the underlay is the coarse picture of the SAME ground the
      // leaves draw, so it is drawn on their datum and not on the registry's
      // mean radius.
      deps.bodyScene.state.orrery?.setDrawRadius(body, manifest.datum_radius_m);
      deps.live.cameraDirty = true;
      siteAlbedo = createSiteAlbedo({
        radiusM: manifest.datum_radius_m,
        urlFor: (id) => dtmUrl(id),
        anisotropy: renderer.capabilities.getMaxAnisotropy(),
        uploadTexture: (t) => renderer.initTexture(t),
        // The leaves already on screen are drawing inherited MDIM, so the
        // image landing is a rebuild of THOSE leaves and nothing else.
        onReady: (siteId) => remeshTiles(siteLeafKeys(siteId)),
        onError: (id, err) => {
          tileError = `${id} albedo: ${err instanceof Error ? err.message : String(err)}`;
        },
      });
      tileTree = createTileTree({
        body,
        radiusM: manifest.datum_radius_m,
        maxLevel: tileGlobalFloor,
        extensions: treeExtensions(manifest),
        // The body's own shape, not the tree's round-body default.
        ...(tileDebug.legacyHorizon
          ? { horizonFromEye: false }
          : { reliefM: reliefAboveDatumM(body, manifest.datum_radius_m) }),
        ...(ringUnbudgeted ? { reliefRefines: true } : {}),
        ...(ringHole ? { ringHole: true } : {}),
        ...(ringChurn ? { legacyRingQueue: true } : {}),
        // The doc's 1.5 unless an instrument asked for something else. Raising
        // it brings a level boundary in from 236 km to tens of kilometres,
        // which is the only way the seam-check can put one at a workable angle
        // and a workable magnification at the same time.
        ...(tileDebug.splitPx > 0 ? { splitPixelsPerSample: tileDebug.splitPx } : {}),
        fetch: source.fetch,
        decode: source.decode,
        onError: (key, err) => {
          tileError = `${formatTileKey(key)}: ${err instanceof Error ? err.message : String(err)}`;
        },
      });
      // A tree opened while a claim read is already out takes the hold with it:
      // the pyramid is opened from the frame loop and the claim from the tier
      // change, and either can be first.
      if (descentQueue.stats().holding) descentQueue.reapplyHold();
      deps.live.needsRender = true;
    })().catch((err: unknown) => {
      tileError = err instanceof Error ? err.message : String(err);
      console.warn(`[atlas] the ${body} pyramid did not open; the globe is the surface:`, err);
    });
  }

  /**
   * The camera as the tree reads it.
   *
   * `cameraM` is body-fixed metres: `cameraWorld` and Mars's centre are both
   * heliocentric scene metres, and the body's own quaternion takes the
   * difference into the frame the tile grid is addressed in. `altitudeM` comes
   * from the focus camera rather than from `cameraM`, so the horizon and the
   * band cannot disagree on a datum that is not a sphere.
   */
  function tileView(node: OrreryBody): View {
    _tileEye.set(
      cameraWorld.x - node.world.x,
      cameraWorld.y - node.world.y,
      cameraWorld.z - node.world.z,
    ).applyQuaternion(_tileQ.copy(node.quaternion).invert());
    // In cruise the pane is display:none and this camera is a proxy carrying
    // the chase rig's projection, so the ruler is the drawn canvas and the fov
    // handed in with it.
    const cruiseAlt = cruiseAltitudeM();
    const cruise = deps.worldHostState.externalWorld && cruiseAlt !== null && deps.worldHostState.externalFovDeg > 0;
    const cameraM = atlasToBody(_tileEye);
    // The camera's own axes in the same body-fixed frame, taken off the
    // rotation the leaves are DRAWN with rather than off `node.quaternion`:
    // below the SURFACE edge `placeFarField` writes the site's own lat/lon
    // basis onto the world frame's root, so the tile grid turns with the
    // walkaround and a frustum built from the body alone points at an
    // arbitrary bearing. Relative to the subtree and not to the scene, because
    // this camera's rotation is the subtree's in both worlds - the focus rig
    // poses it there and the cruise proxy is copied in with the subtree's own
    // rotation already taken out. `atlasToBody`'s swap is a rotation, so a
    // direction goes through it the way a position does.
    _tileCamQ.copy(tileGroup.getWorldQuaternion(_tileGroupQ)).invert()
      .multiply(atlasRoot.getWorldQuaternion(_tileRootQ))
      .multiply(camera.quaternion);
    _tileFwd.set(0, 0, -1).applyQuaternion(_tileCamQ);
    _tileUp.set(0, 1, 0).applyQuaternion(_tileCamQ);
    _tileFwdBody[0] = _tileFwd.x;
    _tileFwdBody[1] = -_tileFwd.z;
    _tileFwdBody[2] = _tileFwd.y;
    _tileUpBody[0] = _tileUp.x;
    _tileUpBody[1] = -_tileUp.z;
    _tileUpBody[2] = _tileUp.y;
    // Off the projection rather than off `fov` and `aspect`: in cruise those
    // two belong to the focus rig and the matrix is the drawn frame's.
    const pm = camera.projectionMatrix.elements;
    frustumPlanes(
      cameraM, _tileFwdBody, _tileUpBody,
      1 / (pm[0] || 1), 1 / (pm[5] || 1), camera.far, _tilePlanes,
    );
    return {
      cameraM,
      frustumM: _tilePlanes,
      fovDeg: cruise ? deps.worldHostState.externalFovDeg : camera.fov,
      // The BASE ratio, not the supersample override: a 4x render is supposed
      // to be the same scene sampled harder, and letting the traversal see the
      // taller viewport would fetch two more levels and compare two different
      // pictures of Mars.
      viewportPx: cruise
        ? Math.max(1, Math.round(deps.dojoPaints.labelViewportH() * renderer.getPixelRatio()))
        : Math.max(1, Math.round(Math.max(1, root.clientHeight) * basePixelRatio())),
      altitudeM: cruise ? cruiseAlt : altitude(),
    };
  }

  /**
   * The region orthoimage over a leaf, or null to keep what it inherited.
   *
   * Only below the global floor: an L7 tile is global MOLA ground and its
   * extent is four times the region's, so the image would be stretched across
   * ground it never covered. `extensionFor` is a shift on the key, not a
   * geometric test, so this costs one map lookup a mesh.
   */
  function siteAlbedoFor(tile: LoadedTile): SiteAlbedoRef | null {
    const manifest = tileSource?.manifest();
    if (!manifest || !siteAlbedo || !tileDebug.siteImage || tile.level <= tileGlobalFloor) return null;
    const ext = extensionFor(manifest, tile.key);
    return ext ? siteAlbedo.refFor(ext.id, tile.bounds) : null;
  }

  /**
   * The drawn leaves whose own extension is this site: what its image landing
   * changes. Every other tile keeps the imagery it already had.
   */
  function siteLeafKeys(siteId?: string): Set<string> {
    const manifest = tileSource?.manifest();
    const out = new Set<string>();
    if (!manifest) return out;
    for (const [id, tile] of tileDrawn) {
      if (tile.level <= tileGlobalFloor) continue;
      const ext = extensionFor(manifest, tile.key);
      if (ext && (siteId === undefined || ext.id === siteId)) out.add(id);
    }
    return out;
  }

  /**
   * How much room the ground panel and telemetry leave clear above the
   * credit, read from the credit's own rendered height rather than assumed -
   * a body whose credit runs to three phrases (Pluto: terrain, imagery and a
   * HiRISE-grade detail line) needs more than the one-line case every other
   * body shows, and this is the one place that has to know it.
   */
  function syncCreditClearance(): void {
    const el = deps.buildPanel.pyramidCreditEl;
    const h = el.classList.contains('hidden') ? 0 : el.offsetHeight;
    el.parentElement?.style.setProperty('--credit-h', `${h}px`);
  }

  function applyCreditVisibility(): void {
    deps.buildPanel.pyramidCreditEl.classList.toggle('hidden', !creditVisible || creditForceHidden);
    syncCreditClearance();
  }

  /**
   * The credit line, from whichever product tiers the drawn set actually pulls
   * from - never a fixed string (docs/roadmap/08-phases.md, Phase 2 exit: "the
   * USGS/NASA credit line is present on screen whenever an MDIM or MOLA tile is
   * visible"). Shown once a global MOLA (terrain) or MDIM (imagery) tile is
   * drawn; a HiRISE detail leaf rides along as a third line when one is also
   * drawn, but never shows alone, since a HiRISE-only screen never happens on
   * this bake (`ATLAS-TERRAIN.md`, "Tile tree contract").
   */
  function updateCredit(): void {
    const manifest = tileSource?.manifest() ?? null;
    const present = new Map<CreditTier, CreditSource>();
    if (manifest && !creditForceBlank) {
      // The MESHED set, not the mounted one: `tileDrawn` carries a tile from
      // the traversal that mounted it, ~180 frames before the build budget has
      // drawn them all, and layout-check gates the line's visibility on the
      // same `drawn` count the stats hook reports off `tileMeshes`.
      for (const [id, tile] of tileDrawn) {
        if (!tileMeshes.has(id)) continue;
        for (const cs of creditSourcesForTile(manifest, tile.key)) {
          if (!present.has(cs.tier)) present.set(cs.tier, cs);
        }
      }
    }
    const tiers = CREDIT_ORDER.filter((t) => present.has(t));
    const mixKey = tiers.map((t) => `${t}:${present.get(t)!.source.url}`).join('|');
    if (mixKey === creditMixKey) return;
    creditMixKey = mixKey;
    creditSourceIds = tiers;
    // The full stop belongs to the phrase, and a cut product already ends one.
    const creditLabel = (t: CreditTier): string => {
      const product = shortProduct(present.get(t)!.source.product);
      return `${CREDIT_WORD[t]} ${product}${product.endsWith('\u2026') ? '' : '.'}`;
    };
    creditText = tiers.map((t) => creditLabel(t)).join(' ');
    creditVisible = tiers.includes('terrain') || tiers.includes('imagery');
    deps.buildPanel.pyramidCreditEl.innerHTML = tiers.map((t) => {
      const src = present.get(t)!.source;
      const label = escapeHtml(creditLabel(t));
      const title = escapeHtml(`${src.product} - ${src.instrument} - ${src.rights}`);
      return src.url
        ? `<a href="${escapeHtml(src.url)}" target="_blank" rel="noopener" title="${title}">${label}</a>`
        : `<span title="${title}">${label}</span>`;
    }).join(' ');
    applyCreditVisibility();
  }

  /**
   * Tiles built before the class maps landed carry no relief: rebuild them,
   * or reopen the tree when the relief changes how deep it has to go.
   */
  function watchTileRelief(): void {
    const body = tileBody;
    if (body === null || tileSource === null) return;
    const version = deps.bodyScene.state.orrery?.byId(body)?.tileReliefVersion ?? -1;
    if (version === tileReliefSeen) return;
    tileReliefSeen = version;
    dropReliefHeights();
    if (smoothGround(body) && (tileReliefFor(body) !== null) !== tileReliefDeep) {
      reopenTilesHeld();
      deps.live.cameraDirty = true;
      tileBody = body;
      ensureTileTree();
      return;
    }
    tileRemeshAll = true;
  }

  function updateTiles(dt: number): void {
    noteTileFrame(dt * 1000);
    tileMountedThisFrame = 0;
    tileReleasedThisFrame = 0;
    tileDrawChanged = false;
    // A body change is a different pyramid, so the open one goes back rather
    // than sitting resident behind whatever is on screen now.
    if (tileBody !== null && state.bodyId !== tileBody) releaseTiles();
    if (tileHeld.size > 0 && state.bodyId !== tileHeldBody) dropHeldTiles();
    const node = deps.bodyScene.state.bodyNode;
    const active = tilesActive();
    if (!active) dropHeldTiles();
    if (!active || !node) {
      // Above the band the traversal stops running, so nothing ever unmounts:
      // ascending from PLOT to BODY SPHERE with Mars still focused would leave
      // every mesh and its albedo on the GPU for a tier that draws none of
      // them. A missing `bodyNode` is a frame of the orrery rebuilding and not
      // a tier change, so that one only hides.
      if (!active && (tileMeshes.size > 0 || tilePending.size > 0 || tileRemesh.size > 0)) {
        releaseTileMeshes();
        deps.live.needsRender = true;
      }
      if (tileGroup.visible) { tileGroup.visible = false; deps.live.needsRender = true; }
      setGlobeUnderlay(false);
      tileAccum = TILE_REQUEST_S;
      if (creditMixKey !== '') {
        creditMixKey = '';
        creditSourceIds = [];
        creditText = '';
        creditVisible = false;
        deps.buildPanel.pyramidCreditEl.innerHTML = '';
        applyCreditVisibility();
      }
      return;
    }
    tileBody = state.bodyId;
    ensureTileTree();
    watchTileRelief();
    tileWorldPos.x = node.world.x;
    tileWorldPos.y = node.world.y;
    tileWorldPos.z = node.world.z;
    tileGroup.quaternion.copy(node.quaternion);

    const tree = tileTree;
    if (tree) {
      tileAccum += dt;
      if (tileAccum >= TILE_REQUEST_S) {
        tileAccum = 0;
        tileWork.traversals++;
        const traverseT0 = performance.now();
        // Every meshed tile is held, since a retired mesh stays on screen past
        // its unmount and an eviction would take it before its replacement is built.
        const update = tree.request(tileView(node), tileMeshes);
        tileQuietPasses = update.mount.length === 0 && update.unmount.length === 0 && tree.stats().complete
          ? tileQuietPasses + 1 : 0;
        const lastDraw = tileDraw;
        tileDraw = update.draw;
        if (update.mount.length > 0 || update.unmount.length > 0) {
          tileDrawChanged = true;
          tileHeightVersion++;
        }
        for (const tile of update.mount) {
          const id = formatTileKey(tile.key);
          tileDrawn.set(id, tile);
          tileWork.mounted++;
          // What `imagery-floor` reads: ground drawn from an elevation payload,
          // counted where the payload arrives rather than off the role list.
          if (tile.height !== null) tileHeightMounts++;
          tileStepDraw.delete(id);
          // Retired and wanted on screen again: the tree came back to this
          // level while the mesh was still covering its own ground, so it is
          // drawn again as it stands rather than built a second time.
          if (tileRetired.delete(id)) continue;
          tilePending.set(id, tile);
        }
        // A kept tile's morph moves every pass, and its albedo provider can
        // change if the ancestor it was borrowing from went away - the uv
        // rectangle is baked into the geometry, so that one is a rebuild.
        for (const tile of update.keep) {
          const id = formatTileKey(tile.key);
          const handle = tileMeshes.get(id);
          const state = update.draw.get(id);
          if (!handle || !state) continue;
          // Both providers when the leaf samples both, the way `createTileMesh`
          // names them: the inherited one can go away under a drawn tile.
          const inheritedId = state.albedo ? formatTileKey(state.albedo.from.key) : null;
          const siteId = siteAlbedoFor(tile)?.id ?? null;
          const want = siteId === null ? inheritedId
            : tileDebug.orthoTint && inheritedId !== null ? `${siteId}|${inheritedId}` : siteId;
          // The morph moves every pass and the swap can wait several frames
          // behind the mount budget, so it is applied first: a queued retarget
          // would otherwise freeze this tile's shape at the boundary with its
          // parent for as long as the queue takes to reach it.
          handle.setMorph(state.morph);
          // The height provider moves the same way the albedo one does: a
          // deeper ancestor arriving under a leaf below the height floor is
          // finer relief, and the field is sampled into the vertices.
          const heightWant = heightRefFor(id)?.from.key;
          const artWant = state.artificial ? formatTileKey(state.artificial.from.key) : null;
          if (handle.albedoFromId !== want
            || handle.artificialFromId !== artWant
            || handle.heightFromId !== (heightWant ? formatTileKey(heightWant) : null)) {
            if (!tileRemesh.has(id)) tileWork.retargeted++;
            tileRemesh.set(id, tile);
          }
        }
        // Hidden and evicted are both a mesh gone here: the resident set runs to
        // 800 MB, and holding geometry and a 1 MB texture for every tile in it
        // rather than for the ones on screen would be past the 512 MB GPU
        // budget. Re-meshing a tile that comes back is a fraction of a
        // millisecond, which is the cheaper mistake.
        const stepCandidates: string[] = [];
        const keepSteps = tileDebug.holdSplits && !tileDebug.wholeRelease;
        for (const tile of update.unmount) {
          const id = formatTileKey(tile.key);
          tileRemesh.delete(id);
          tileDrawn.delete(id);
          if (keepSteps && tilePending.has(id)) { stepCandidates.push(id); continue; }
          tilePending.delete(id);
          // A mesh on screen goes on covering its ground past its unmount: what
          // replaces it is built two a frame.
          if (tileDebug.holdSplits && tileMeshes.has(id)) {
            if (!tileHidden.has(id)) { tileRetired.add(id); continue; }
            if (keepSteps) { stepCandidates.push(id); continue; }
          }
          dropTileMesh(id);
        }
        // One never revealed, built or still queued, is kept as a step of the
        // cover over it, and one under no cover is covering nothing. Read after
        // the loop, since the tree lists a cover and the tiles under it in no
        // particular order.
        for (const id of stepCandidates) {
          const step = anyAbove(id, tileRetired);
          const last = lastDraw.get(id);
          if (tileMeshes.has(id)) {
            if (step) tileRetired.add(id);
            else dropTileMesh(id);
          } else if (step && last) {
            tileStepDraw.set(id, last);
          } else {
            tilePending.delete(id);
          }
        }
        for (const tile of update.evict) {
          const id = formatTileKey(tile.key);
          tileStepDraw.delete(id);
          tilePending.delete(id);
          tileRemesh.delete(id);
          tileDrawn.delete(id);
          forgetReliefHeights(id);
          tile.dispose();
          tileWork.evicted++;
        }
        // The draw set has just moved, so what a retired mesh is still the only
        // cover for has too. Before `updateCredit`, which reads the meshed set.
        resolveTileCover();
        updateCredit();
        tileWork.traverseMs += performance.now() - traverseT0;
      }
    }

    // Coalesced: a slider drag fires an input event a frame and each one only
    // has to say that the whole set is stale, not walk it.
    if (tileRemeshAll) {
      tileRemeshAll = false;
      remeshTiles();
    }
    let built = 0;
    const buildT0 = performance.now();
    // The frame's slice, asked before a build and never inside one. The first
    // build of a frame always goes: a leaf costlier than the whole slice would
    // otherwise never be mounted at all.
    const fitsSlice = (): boolean => {
      if (tileBuildUnbudgeted) return true;
      if (built >= TILE_MOUNTS_PER_FRAME) return false;
      return built === 0 || tileBuildSliceOff
        || performance.now() - buildT0 + tileBuildCostMs <= TILE_BUILD_SLICE_MS;
    };
    askReliefHeights();
    // Mounts before swaps: a pending mount is a hole in the picture, while a
    // pending swap is ground already drawn with a parameter one step behind.
    for (const [id, tile] of tilePending) {
      const step = tileStepDraw.has(id);
      // A step whose cover has already gone would be a cover of its own.
      if (step && (tileDebug.wholeRelease || !anyAbove(id, tileRetired))) {
        tilePending.delete(id);
        tileStepDraw.delete(id);
        continue;
      }
      if (reliefWaiting(id, tile)) continue;
      if (!fitsSlice()) break;
      const meshT0 = performance.now();
      tilePending.delete(id);
      tileRemesh.delete(id);
      const handle = buildTileMesh(id, tile);
      tileStepDraw.delete(id);
      if (handle && step) tileRetired.add(id);
      if (handle) {
        // Invisible if a retired mesh is still covering this ground;
        // `resolveTileCover` below reveals it on the frame that one is dropped.
        if (tileRetired.size > 0
          && (anyAbove(id, tileRetired) || atOrBelow(tileRetired).has(id))) {
          handle.object.visible = false;
          tileHidden.add(id);
        }
        warmTileMesh(handle);
        tileGroup.add(handle.object);
        tileMeshes.set(id, handle);
        tileTriangles += handle.triangles;
      }
      built++;
      chargeTileBuild(meshT0);
    }
    for (const [id, tile] of tileRemesh) {
      if (reliefWaiting(id, tile)) continue;
      if (!fitsSlice()) break;
      const meshT0 = performance.now();
      tileRemesh.delete(id);
      const hidden = tileHidden.has(id);
      const handle = buildTileMesh(id, tile);
      if (handle) {
        // Added before the old one goes: the ground under this key is never
        // uncovered, which a drop-then-rebuild leaves it for as long as the
        // per-frame budget takes to reach it. A mesh kept invisible by a
        // retired one stays invisible across the rebuild, and `dropTileMesh`
        // clears that flag, so it is read first and set again after.
        handle.object.visible = !hidden;
        warmTileMesh(handle);
        tileGroup.add(handle.object);
        dropTileMesh(id);
        if (hidden) tileHidden.add(id);
        tileMeshes.set(id, handle);
        tileTriangles += handle.triangles;
      }
      built++;
      chargeTileBuild(meshT0);
    }
    tileMountedThisFrame = built;
    if (built > 0) {
      const heldMs = performance.now() - buildT0;
      if (heldMs > tileBuildFrameWorstMs) {
        tileBuildFrameWorstMs = heldMs;
        tileBuildFrameWorstBuilt = built;
      }
      tileBuildFrames++;
      // The slice and not the count: a frame the count stopped was already
      // budgeted before this, and only the slice's own cuts say the ms bound
      // did anything.
      if (!tileBuildUnbudgeted && built < TILE_MOUNTS_PER_FRAME
        && (tilePending.size > 0 || tileRemesh.size > 0)) tileBuildDeferred++;
      // The hex drape reads the MESHES, and a mount is budgeted per frame: the
      // traversal bumped the revision frames before the ground it promised
      // arrived here, so the grid rebuilt against ground that was not up yet.
      tileHeightVersion++;
      // A build is what a retired mesh has been waiting for, and the traversal
      // that would notice is 100 ms away.
      resolveTileCover();
      tileWork.buildMs += performance.now() - buildT0;
      // A mesh built and uploaded is the sim's cost, not the GPU's.
      markLoad();
      deps.live.needsRender = true;
      // The line reads the meshed set, which these two loops just moved, and a
      // traversal is 100 ms away - six frames of a line disagreeing with the
      // gate that reads the same set.
      updateCredit();
    } else if (tileCoverDirty) {
      // The steps a cover uncovered, and the covers the cap held back, go on
      // the frames after it rather than at the next traversal.
      resolveTileCover();
    }
    const wanted = deps.farField.farFieldWanted();
    if (deps.injects.state.legacyPlotFade) {
      farFieldHeld = false;
    } else if (wanted) {
      farFieldHeld = true;
    } else if (deps.ladder.orreryOn) {
      farFieldHeld = false;
    }
    if (tileHeld.size > 0) {
      // Both sets drawn for a few frames: the eye was stood on the held ground,
      // and new ground higher than that is seen from inside until it re-stands.
      if (tileHeldOverlap > 0) {
        if (++tileHeldOverlap > TILE_HOLD_OVERLAP_FRAMES) dropHeldTiles();
      } else if (tileSetSettled() || performance.now() - tileHeldSince > TILE_HOLD_MAX_MS) {
        for (const [id, h] of tileMeshes) h.object.visible = !tileHidden.has(id);
        tileHeldOverlap = 1;
        tileHeightVersion++;
        deps.live.cameraDirty = true;
      } else for (const h of tileMeshes.values()) h.object.visible = false;
    }
    const show = (deps.ladder.orreryOn || wanted || farFieldHeld) && (tileMeshes.size > 0 || tileHeld.size > 0);
    // The underlay follows the tiles EXISTING, not their being drawn: with the
    // pyramid held out of frame the globe is still the picture under it, which
    // is the state `--occlusion` reads and every descent passes through while
    // its tiles arrive.
    const draw = show && !occlusionDebug.pyramidHidden;
    if (tileGroup.visible !== draw) { tileGroup.visible = draw; deps.live.needsRender = true; }
    setGlobeUnderlay(show);
  }

  /**
   * Rebuild drawn tiles: every one, or only the named ones.
   *
   * Queued as a SWAP, not a drop - each mesh stays on screen until its
   * replacement is built, spread over the same per-frame budget a mount is.
   */
  function remeshTiles(only?: ReadonlySet<string>): void {
    for (const [id, tile] of tileDrawn) {
      if (only && !only.has(id)) continue;
      // Its first mesh has not been built yet, and will be built from the new
      // parameters when it is - queueing a swap would build it twice.
      if (tilePending.has(id)) continue;
      if (!tileRemesh.has(id)) tileWork.remeshed++;
      tileRemesh.set(id, tile);
    }
    deps.live.needsRender = true;
  }

  /**
   * `groundRadiusUnderInner`'s own hold, separate from `focusGroundM`'s: keyed
   * to the tile footprint under the QUERY POINT rather than a fixed anchor,
   * since the eye that calls it moves every frame. Holds the last accepted
   * `{h, level}` and the tile id whose footprint it is good for; a query
   * still inside that footprint only advances to a reading from a tile at
   * least as fine, and a frame with nothing resident there keeps this instead
   * of falling back to the datum. Dropped when the query leaves the held
   * footprint, the body changes, or the plot mounts or releases.
   */
  let groundHoldTileId: string | null = null;
  let groundHoldLevel = -1;
  let groundHoldH = 0;
  let groundHoldBody: string | null = null;
  let groundHoldPlotMounted = false;

  /**
   * How far the ground `groundRadiusUnderInner` REPORTS is still held above the
   * reading the tiles now give, metres.
   *
   * A finer tile's first answer where the coarse pyramid was only a magnified
   * parent texel differs from it by hundreds of metres, and the eye stands
   * 1.7 m over whatever answers - taken straight, one tile arrival moves the
   * rendered eye that far in one frame. Absorbed here instead, so the reported
   * radius is continuous across the refinement, and closed at
   * GROUND_GLIDE_M_PER_S. Only ever positive: a correction may leave the eye
   * over the drawn ground while it closes, never inside it.
   */
  let groundGlideM = 0;
  /** Where the held reading was taken, for the stillness test below. */
  let groundHoldLat = 0;
  let groundHoldLon = 0;
  /**
   * How far the query point may have moved and still count as the same point.
   *
   * Only a reading that changes under a STILL eye is a correction. A level-0
   * footprint is a whole quad face, so an eye descending across one reads
   * thousands of kilometres of real terrain inside it - absorbing that as a
   * correction held 5.3 km of glide over one descent and left the eye minutes
   * from the ground.
   */
  const GROUND_GLIDE_STILL_M = 25;

  /**
   * Elevation under a body-fixed lat/lon, from the deepest MOUNTED leaf.
   *
   * Metres above the datum, or null where the pyramid draws nothing over this
   * point yet. The address is computed and looked up rather than searched for,
   * so this costs one map probe a level and not one per resident tile.
   *
   * Read off the mesh's own 33-vertex lattice and not the 256-sample field
   * behind it (`meshHeightAtLatLon`): the two disagree by the relief inside one
   * mesh quad, either sign, and this is what a camera is stood on. A tile whose
   * mesh is not built, or is hidden under a coarser one, is skipped for the
   * same reason - a reading is about what is drawn.
   */
  function tileHeightAt(latDeg: number, lonDeg: number): number | null {
    return tileHeightProbe(latDeg, lonDeg, false)?.h ?? null;
  }

  /**
   * The same probe, taken off the ground the leaves DRAW.
   *
   * What the hex overlay drapes onto. Two things stand between the height
   * field and the surface on screen: the mesh is 32 quads across a 256-sample
   * field, and a tile that has just split draws part way on its parent's
   * lattice (`drawnRadiusAt` in tileMesh.ts). Reading the field instead put
   * hex edges hundreds of metres under the terrain.
   */
  function tileMeshHeightAt(latDeg: number, lonDeg: number): number | null {
    return tileHeightProbe(latDeg, lonDeg, true)?.h ?? null;
  }

  /**
   * `tileHeightAt`/`tileMeshHeightAt`'s shared probe, with the level that
   * answered attached - `updateFocusGround` needs the level to tell a finer
   * tile's arrival apart from a coarser one transiently standing in for it.
   * `id` is the raw addressed tile key at the loop level that answered (not
   * the ref-adjusted `level`), which is the footprint `groundRadiusUnderInner`
   * holds its own reading against.
   */
  function tileHeightProbe(
    latDeg: number,
    lonDeg: number,
    mesh: boolean,
  ): { h: number; level: number; id: string } | null {
    const body = tileBody;
    if (body === null || (tileDrawn.size === 0 && tileHeld.size === 0)) return null;
    const latRad = (latDeg * Math.PI) / 180;
    const lonRad = (lonDeg * Math.PI) / 180;
    // Smooth ground's procedural relief lives only in the mesh, so its field read
    // is the drawn one too: the datum put the focus anchor a kilometre inside the hills.
    const meshOnly = !deps.injects.state.reliefDatumFocus && smoothGround(body) && tileReliefFor(body) !== null;
    const dir = mesh || meshOnly ? directionForLatLon(latRad, lonRad) : null;
    for (let level = Math.max(tileMaxLevel, tileHeldMaxLevel); level >= 0; level--) {
      const id = formatTileKey(tileForLatLon(body, latRad, lonRad, level));
      if (dir && (meshOnly || !deps.injects.state.fieldDrapeDebug)) {
        // The mesh's own vertices, geomorph included: the exaggeration comes
        // back out because every caller here works in unstretched metres.
        // Only a VISIBLE mesh answers - `tileDrawn` is the meshed set and holds
        // tiles a retired ancestor is still covering, which read 300 m off the
        // ground on screen at LOCAL on Mars.
        const mesh = drawnTileMesh(id);
        if (mesh) {
          const r = mesh.drawnRadiusAt(dir);
          if (Number.isFinite(r)) {
            return { h: (r - leafDatumM()) / Math.max(1e-6, solar.exag), level, id };
          }
        }
      }
      const tile = tileDrawn.get(id);
      if (!tile) continue;
      if (!tileMeshes.has(id) || tileHidden.has(id)) continue;
      // The leaf's own field, or the ancestor its MESH was built from: reading
      // a different one would put the clearance over ground nothing draws.
      const ref = tile.height ? null : heightRefFor(id);
      const from = tile.height ? tile : ref?.from ?? null;
      if (!from?.height) continue;
      const leaf = { bounds: from.bounds, height: from.height };
      const h = mesh
        ? leafMeshHeightAtLatLon(leaf, latRad, lonRad, TILE_MESH_QUADS, tile.bounds)
        : deps.injects.state.legacyFieldHeight
          ? leafHeightAtLatLon(leaf, latRad, lonRad)
          : meshHeightAtLatLon(tile.bounds, tile, ref, latRad, lonRad);
      // A tile borrowing an ancestor's relief (`ref` set) reads at the
      // ancestor's own level, not its own - it is that coarse until its own
      // field lands, and reporting its nominal level would hide the later
      // jump from the borrowed reading to its own as "no change in level".
      if (Number.isFinite(h)) return { h, level: ref ? level - ref.levelsUp : level, id };
    }
    return null;
  }

  /**
   * The imagery drawn over a point: the tile whose albedo the deepest drawn
   * leaf shows there, its own or an ancestor's, and the texel position in it
   * (row 0 at the v0 edge, the way `createTileMesh` reads it).
   */
  function tileAlbedoAt(latDeg: number, lonDeg: number): { from: LoadedTile; s: number; t: number } | null {
    const body = tileBody;
    if (body === null || tileDrawn.size === 0 || typeof ImageBitmap === 'undefined') return null;
    const latRad = (latDeg * Math.PI) / 180;
    const lonRad = (lonDeg * Math.PI) / 180;
    const f = faceUvForDirection(directionForLatLon(latRad, lonRad));
    for (let level = tileMaxLevel; level >= 0; level--) {
      const id = formatTileKey(tileForLatLon(body, latRad, lonRad, level));
      const tile = tileDrawn.get(id);
      if (!tile || !tileMeshes.has(id) || tileHidden.has(id)) continue;
      const from = tile.albedo ? tile : drawStateFor(id)?.albedo?.from ?? null;
      const a = from?.albedo;
      if (!from || !a || !(a.source instanceof ImageBitmap) || from.bounds.face !== f.face) continue;
      const b = from.bounds;
      return {
        from,
        s: ((f.u - b.u0) / (b.u1 - b.u0)) * a.width - 0.5,
        t: ((f.v - b.v0) / (b.v1 - b.v0)) * a.height - 0.5,
      };
    }
    return null;
  }

  /** A drawn tile's state, or the one a queued step last had. */
  function drawStateFor(id: string): TileDrawState | undefined {
    return tileDraw.get(id) ?? tileStepDraw.get(id);
  }

  /** The ancestor a drawn leaf borrows relief from, or null when it has its own. */
  function heightRefFor(id: string): TileHeightRef | null {
    if (!tileDebug.heightFallback) return null;
    return drawStateFor(id)?.height ?? null;
  }

  /**
   * Radius of the DRAWN ground under a heliocentric point, metres.
   *
   * The pyramid's own height field at the point's body-fixed lat/lon, on the
   * datum the leaves are built on - which is what "above the terrain" has to
   * mean on a body whose relief is 29 km. Null while the plot's local frame owns
   * the camera, where the focus camera's pitch is not what is drawn and moving
   * it would fight the walkaround's own floor - but a band is not a walkaround
   * on its own: with no plot mesh loaded the leaves are still the ground, and
   * the SURFACE band is where the eye is closest to them.
   */
  function groundRadiusUnder(id: string, p: WorldVec): number | null {
    const probeT0 = performance.now();
    tileWork.probes++;
    try {
      return groundRadiusUnderInner(id, p);
    } finally {
      tileWork.probeMs += performance.now() - probeT0;
    }
  }

  /** The visible mesh's drawn radius at a body-fixed lat/lon, finest level first; null where none draws. */
  function drawnMeshRadius(id: string, latRad: number, lonRad: number): number | null {
    const dir = directionForLatLon(latRad, lonRad);
    for (let level = Math.max(tileMaxLevel, tileHeldMaxLevel); level >= 0; level--) {
      const mesh = drawnTileMesh(formatTileKey(tileForLatLon(id, latRad, lonRad, level)));
      if (!mesh) continue;
      const r = mesh.drawnRadiusAt(dir);
      if (Number.isFinite(r)) return r;
    }
    return null;
  }

  /** The same radius under a body-fixed unit direction, and the datum where none draws. */
  function drawnReliefRadius(id: string, x: number, y: number, z: number): number {
    return drawnMeshRadius(id, Math.asin(THREE.MathUtils.clamp(y, -1, 1)), Math.atan2(-z, x)) ?? leafDatumM();
  }

  /**
   * How far along a ray, absolute scene axes, the ground the tiles draw is
   * first met: null on a miss, undefined while no relief is displaced, where
   * the position buffers a raycast reads are already what is drawn.
   *
   * The relief is added in the vertex shader, so this marches the ray against
   * `drawnReliefRadius` between the relief's own shells and bisects the crossing.
   */
  function drawnGroundHit(origin: WorldVec, dir: WorldVec, far: number): number | null | undefined {
    const id = tileBody;
    const node = deps.bodyScene.state.bodyNode;
    if (id === null || !node || tileDrawn.size === 0 || tileReliefOn.value <= 0) return undefined;
    if (!smoothGround(id) || tileReliefFor(id) === null) return undefined;
    if (deps.cameraRig.surfaceRegime() && deps.live.plot !== null) return undefined;
    let lo = 0;
    let hi = 0;
    let displaced = false;
    for (const h of [...tileMeshes.values(), ...tileHeld.values()]) {
      if (!h.reliefDisplaced || !h.object.visible) continue;
      displaced = true;
      if (h.reliefLoM < lo) lo = h.reliefLoM;
      if (h.reliefHiM > hi) hi = h.reliefHiM;
    }
    if (!displaced) return undefined;
    const datum = leafDatumM();
    const rLo = datum + lo * tileReliefOn.value - 1;
    const rHi = datum + hi * tileReliefOn.value + 1;
    _rayQ.copy(node.quaternion).invert();
    const o = _rayO.set(origin.x - node.world.x, origin.y - node.world.y, origin.z - node.world.z).applyQuaternion(_rayQ);
    const d = _rayD.set(dir.x, dir.y, dir.z).applyQuaternion(_rayQ).normalize();
    const b = o.dot(d);
    const oo = o.lengthSq();
    const discHi = b * b - (oo - rHi * rHi);
    if (discHi < 0) return null;
    const t0 = Math.max(0, -b - Math.sqrt(discHi));
    let t1 = Math.min(far, -b + Math.sqrt(discHi));
    const discLo = b * b - (oo - rLo * rLo);
    if (discLo >= 0 && -b - Math.sqrt(discLo) > 0) t1 = Math.min(t1, -b - Math.sqrt(discLo));
    if (t0 > t1) return null;
    const above = (t: number): number => {
      const px = o.x + d.x * t;
      const py = o.y + d.y * t;
      const pz = o.z + d.z * t;
      const r = Math.hypot(px, py, pz);
      return r === 0 ? -1 : r - drawnReliefRadius(id, px / r, py / r, pz / r);
    };
    let tPrev = t0;
    let fPrev = above(t0);
    if (fPrev <= 0) return t0;
    // Half the clearance is a safe stride on slopes up to about 60 degrees;
    // the floor grows with range, where a pixel covers more ground anyway.
    for (let i = 0; i < 4096; i++) {
      if (tPrev >= t1) return null;
      const t = Math.min(t1, tPrev + Math.max(0.5, 0.002 * tPrev, 0.5 * fPrev));
      const f = above(t);
      if (f <= 0) {
        let a = tPrev;
        let c = t;
        for (let k = 0; k < 32 && c - a > 0.01; k++) {
          const m = 0.5 * (a + c);
          if (above(m) > 0) a = m; else c = m;
        }
        return c;
      }
      tPrev = t;
      fPrev = f;
    }
    return undefined;
  }

  function groundRadiusUnderInner(id: string, p: WorldVec): number | null {
    const node = deps.bodyScene.state.bodyNode;
    if (id !== tileBody || !node || tileDrawn.size === 0) return null;
    if (deps.cameraRig.surfaceRegime() && deps.live.plot !== null) return null;
    // Smooth ground carries no height field at any level - its leaves are the
    // datum sphere under their ancestor's imagery, drawn at one constant
    // radius everywhere - so `tileHeightProbe`'s per-tile height lookup below
    // would never answer and the eye would never find ground under it.
    if (smoothGround(id)) {
      if (tileReliefFor(id) === null || deps.injects.state.reliefDatumEye) return leafDatumM();
      _v.set(p.x - node.world.x, p.y - node.world.y, p.z - node.world.z)
        .applyQuaternion(_q.copy(node.quaternion).invert());
      if (_v.lengthSq() === 0) return leafDatumM();
      _v.normalize();
      return drawnReliefRadius(id, _v.x, _v.y, _v.z);
    }
    _v.set(p.x - node.world.x, p.y - node.world.y, p.z - node.world.z)
      .applyQuaternion(_q.copy(node.quaternion).invert());
    const r = _v.length();
    if (r === 0) return null;
    _v.divideScalar(r);
    const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_v.y, -1, 1)));
    let lon = THREE.MathUtils.radToDeg(Math.atan2(-_v.z, _v.x));
    if (lon < 0) lon += 360;

    // The plot's own mount state, not just its presence: `surfaceRegime() &&
    // plot !== null` already returned above, but the hold has to drop on the
    // transition either way round so a reading from one regime never answers
    // the other's first query.
    const plotMounted = deps.live.plot !== null;
    if (id !== groundHoldBody || plotMounted !== groundHoldPlotMounted) {
      groundHoldBody = id;
      groundHoldPlotMounted = plotMounted;
      groundHoldTileId = null;
      groundHoldLevel = -1;
      groundGlideM = 0;
    }

    // Is the query point still inside the held tile's own footprint? Pure
    // addressing, independent of whether that tile (or any tile) is resident
    // this frame - a resident-tile test can't tell "still over the same
    // ground, no finer data yet" apart from "moved to new ground nobody has
    // read".
    let insideHeld = false;
    if (groundHoldTileId !== null) {
      const held = parseTileKey(groundHoldTileId);
      const latRad = (lat * Math.PI) / 180;
      const lonRad = (lon * Math.PI) / 180;
      insideHeld =
        formatTileKey(tileForLatLon(id, latRad, lonRad, held.level)) === groundHoldTileId;
    }

    const probe = tileHeightProbe(lat, lon, false);
    if (groundHoldTileId === null || !insideHeld) {
      // No hold, or the query left the held footprint: whatever answers now
      // is the only candidate for this ground, coarse or not.
      if (probe) {
        groundHoldTileId = probe.id;
        groundHoldLevel = probe.level;
        groundHoldH = probe.h;
        groundHoldLat = lat;
        groundHoldLon = lon;
      } else {
        groundHoldTileId = null;
        groundHoldLevel = -1;
        // A split's retired cover still draws this ground while its replacement
        // builds, and the probe reads mounted tiles only.
        return deps.injects.state.BLIND_COVER ? null : drawnMeshRadius(id, (lat * Math.PI) / 180, (lon * Math.PI) / 180);
      }
    } else if (probe && probe.level >= groundHoldLevel) {
      // The refinement: the same ground, read off a tile at least as fine as
      // the one already held. Absorbed into the glide rather than returned, so
      // this frame's radius is the last one and the step leaves over the next
      // seconds. A reading that comes back up spends the glide first, since the
      // eye may not be put under the ground to close a correction faster.
      // Ground that reads differently because the point MOVED is not a
      // correction and is taken as it comes.
      let dLon = lon - groundHoldLon;
      if (dLon > 180) dLon -= 360;
      if (dLon < -180) dLon += 360;
      const movedM = leafDatumM() * Math.hypot(
        ((lat - groundHoldLat) * Math.PI) / 180,
        ((dLon * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180),
      );
      if (movedM <= GROUND_GLIDE_STILL_M) {
        groundGlideM = Math.max(0, groundGlideM + (groundHoldH - probe.h) * solar.exag);
      }
      groundHoldTileId = probe.id;
      groundHoldLevel = probe.level;
      groundHoldH = probe.h;
      groundHoldLat = lat;
      groundHoldLon = lon;
    }
    // Otherwise: still over the held footprint and nothing at least as fine
    // answered this frame (nothing resident, or a coarser tile stood in) -
    // hold the last accepted reading instead of chasing it back to the datum.
    return leafDatumM() + groundHoldH * solar.exag + groundGlideM;
  }

  /**
   * Walk the held ground correction off at the stated rate.
   *
   * Exponential once the remainder is small, so the eye comes to rest the way
   * `focusGroundM` does and a settled frame is byte-identical, speed-capped
   * above that - which is the whole point: a 273 m correction is seconds of
   * glide, not one frame of jump.
   */
  function updateGroundGlide(dt: number): void {
    if (groundGlideM <= 0) return;
    if (groundGlideM < 0.01) {
      groundGlideM = 0;
      deps.live.cameraDirty = true;
      return;
    }
    const ease = groundGlideM * (1 - Math.exp(-dt * 3));
    groundGlideM -= Math.min(groundGlideM, Math.min(ease, GROUND_GLIDE_M_PER_S * dt));
    deps.live.cameraDirty = true;
  }

  function spendGroundGlide(m: number): void {
    if (!(m > 0) || groundGlideM <= 0) return;
    groundGlideM = Math.max(0, groundGlideM - m);
    deps.live.cameraDirty = true;
  }

  /** `tileCoverage`'s own target, key material and readback buffer. */
  let coverProbeCache: {
    w: number; h: number; rt: THREE.WebGLRenderTarget;
    key: THREE.MeshBasicMaterial; px: Uint8Array;
    disc: Uint8Array; through: Uint8Array; seen: Uint8Array; queue: Int32Array;
  } | null = null;
  function coverProbe(w: number, h: number): NonNullable<typeof coverProbeCache> {
    if (coverProbeCache && coverProbeCache.w === w && coverProbeCache.h === h) {
      return coverProbeCache;
    }
    coverProbeCache?.rt.dispose();
    coverProbeCache?.key.dispose();
    const rt = new THREE.WebGLRenderTarget(w, h);
    rt.texture.name = 'tile-coverage-probe';
    const key = new THREE.MeshBasicMaterial({ color: 0xff00ff });
    key.onBeforeCompile = (shader): void => injectGlobeDepthPush(shader);
    key.customProgramCacheKey = (): string => 'fxciv-cover-key';
    coverProbeCache = {
      w, h, rt, key,
      px: new Uint8Array(w * h * 4),
      disc: new Uint8Array(w * h),
      through: new Uint8Array(w * h),
      seen: new Uint8Array(w * h),
      queue: new Int32Array(w * h),
    };
    return coverProbeCache;
  }

  /**
   * Keyed pixels no flood from the frame's border can reach, over the mask of
   * everything a leaf does NOT cover.
   *
   * Four-connected, iterative, one pass over a 192-wide frame - cheap enough to
   * run every frame of a held stop, which is what the reading needs.
   */
  function enclosedKeyed(
    probe: NonNullable<typeof coverProbeCache>, w: number, h: number,
  ): number {
    const { disc, through, seen, queue } = probe;
    seen.fill(0);
    let head = 0;
    let tail = 0;
    // Open where the datum shows through, and everywhere off the disc: a leaf
    // covers neither, so the flood runs through both.
    const open = (i: number): boolean => through[i] === 1 || disc[i] === 0;
    const push = (i: number): void => {
      if (seen[i] === 1 || !open(i)) return;
      seen[i] = 1;
      queue[tail++] = i;
    };
    for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
    while (head < tail) {
      const i = queue[head++]!;
      const x = i % w;
      const y = (i - x) / w;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (y > 0) push(i - w);
      if (y < h - 1) push(i + w);
    }
    let n = 0;
    for (let i = 0; i < w * h; i++) if (through[i] === 1 && seen[i] === 0) n++;
    return n;
  }
  own(() => {
    coverProbeCache?.rt.dispose();
    coverProbeCache?.key.dispose();
    coverProbeCache = null;
  });

  /** Memo for `parentKeyId`: a staged release walks every mesh's chain each frame. */
  const parentIds = new Map<string, string | null>();
  /** A key's parent, as a key STRING: the hold pairing walks ancestors per tile. */
  function parentKeyId(id: string): string | null {
    const known = parentIds.get(id);
    if (known !== undefined) return known;
    const p = id.split('/');
    const level = Number(p[2]);
    const parent = level > 0
      ? `${p[0]}/${p[1]}/${level - 1}/${Number(p[3]) >> 1}/${Number(p[4]) >> 1}` : null;
    if (parentIds.size >= 65536) parentIds.clear();
    parentIds.set(id, parent);
    return parent;
  }

  /** Every key in `keys` and every ancestor of one: "is there one at or below". */
  function atOrBelow(keys: Iterable<string>): Set<string> {
    const out = new Set<string>();
    for (const k of keys) {
      for (let up: string | null = k; up !== null; up = parentKeyId(up)) {
        if (out.has(up)) break;
        out.add(up);
      }
    }
    return out;
  }

  /** Is any STRICT ancestor of `id` in `keys`? */
  function anyAbove(id: string, keys: { has(key: string): boolean }): boolean {
    for (let up = parentKeyId(id); up !== null; up = parentKeyId(up)) {
      if (keys.has(up)) return true;
    }
    return false;
  }

  /**
   * Which retired meshes can go now, and which built ones can be seen.
   *
   * Derived from the draw set on every pass rather than remembered as a group
   * of outgoing and incoming tiles. The tree changes its mind mid-build - an
   * incoming tile splits again, or is evicted - and a remembered group then
   * waits for a tile that is never coming: one face's L0 mesh stayed on screen
   * under its own grandchildren for a whole descent that way, which
   * `tile-swap-check.mjs` reads as `overlap` and as a hold outliving its queue.
   * Re-deriving cannot go stale, and the definition of covered here is the one
   * the instrument measures against.
   */
  function resolveTileCover(): void {
    tileCoverDirty = false;
    tileReleaseWaiting = 0;
    if (tileRetired.size === 0 && tileHidden.size === 0) return;
    const coverT0 = performance.now();
    if (tileRetired.size > 0) {
      // Ground the replacement has not covered yet: drawn, unmeshed, and a
      // build queued to give it one. Drawn, unmeshed and NOT queued is a build
      // that threw - never arriving, so it never holds a retired mesh open.
      const blockers = new Set<string>();
      for (const id of tileDrawn.keys()) {
        if (tileMeshes.has(id)) continue;
        if (tilePending.has(id) || tileRemesh.has(id)) blockers.add(id);
      }
      if (tileDebug.wholeRelease) {
        const blockedBelow = atOrBelow(blockers);
        for (const id of [...tileRetired]) {
          // A blocker under a retired mesh is ground the retired mesh is still
          // the only cover for; one above it is the merge direction, where the
          // coarse tile that replaces four children is itself not meshed yet.
          if (blockedBelow.has(id) || anyAbove(id, blockers)) continue;
          dropTileMesh(id);
        }
      } else {
        releaseInSteps(blockers);
      }
    }
    const retiredBelow = atOrBelow(tileRetired);
    let changed = false;
    let revealed = 0;
    for (const [id, handle] of tileMeshes) {
      // A retired mesh is a cover when no coarser retired one is over it, and
      // a step of that one's release otherwise.
      const retired = tileRetired.has(id);
      if (retired && tileDebug.wholeRelease) continue;
      const covered = retired
        ? anyAbove(id, tileRetired)
        : tileRetired.size > 0 && (retiredBelow.has(id) || anyAbove(id, tileRetired));
      if (covered === tileHidden.has(id)) continue;
      if (covered) tileHidden.add(id);
      else { tileHidden.delete(id); revealed++; }
      handle.object.visible = !covered;
      changed = true;
    }
    tileReleasedThisFrame += revealed;
    if (tileReleasedThisFrame > tileReleaseFrameWorst) tileReleaseFrameWorst = tileReleasedThisFrame;
    // Which mesh the drape reads under a point, so the grid is drawn again.
    if (changed) { tileHeightVersion++; deps.live.needsRender = true; }
    tileWork.coverMs += performance.now() - coverT0;
  }

  /** The nearest STRICT ancestor of `id` that has a mesh, or null. */
  function meshedAbove(id: string): string | null {
    for (let up = parentKeyId(id); up !== null; up = parentKeyId(up)) {
      if (tileMeshes.has(up)) return up;
    }
    return null;
  }

  /**
   * Drop the covers whose next step down is meshed, coarsest first, until the
   * frame has uncovered `RELEASE_PER_FRAME` meshes.
   *
   * A cover waits only on ground it would leave bare: a blocker whose nearest
   * meshed ancestor it is. A kept step between the two covers that blocker
   * once the cover goes. The retired children of one drawn tile go together,
   * since any one of them going first is a hole beside the other three.
   */
  function releaseInSteps(blockers: ReadonlySet<string>): void {
    // A kept step is worth its memory only while the ground under it is still
    // being refined toward from above.
    const drawnUnder = atOrBelow(tileDrawn.keys());
    for (const id of [...tileRetired]) {
      if (!tileHidden.has(id)) continue;
      if (!drawnUnder.has(id) || anyAbove(id, tileDrawn)) dropTileMesh(id);
    }
    for (const id of [...tileStepDraw.keys()]) {
      if (drawnUnder.has(id) && !anyAbove(id, tileDrawn) && anyAbove(id, tileRetired)) continue;
      tileStepDraw.delete(id);
      tilePending.delete(id);
    }
    const held = new Set<string>();
    for (const b of blockers) {
      const guard = meshedAbove(b);
      if (guard !== null && tileRetired.has(guard)) held.add(guard);
    }
    // The merge direction: the coarse tile replacing these is not meshed yet.
    for (const id of tileRetired) if (anyAbove(id, blockers)) held.add(id);
    // What each cover's going would uncover: the meshes it is the nearest
    // meshed ancestor of.
    const uncovers = new Map<string, number>();
    for (const id of tileMeshes.keys()) {
      const guard = meshedAbove(id);
      if (guard !== null && tileRetired.has(guard)) uncovers.set(guard, (uncovers.get(guard) ?? 0) + 1);
    }
    // More than a frame's worth under a cover waits for a step still queued
    // between the two, which is then all it uncovers.
    for (const id of tileStepDraw.keys()) {
      const guard = meshedAbove(id);
      if (guard !== null && tileRetired.has(guard) && (uncovers.get(guard) ?? 0) > RELEASE_PER_FRAME) {
        held.add(guard);
      }
    }
    type Unit = { level: number; ids: string[]; reveal: number; held: boolean };
    const merges = new Map<string, Unit>();
    const units: Unit[] = [];
    for (const id of tileRetired) {
      if (tileHidden.has(id)) continue;
      let drawnAbove: string | null = null;
      for (let up = parentKeyId(id); up !== null; up = parentKeyId(up)) {
        if (tileDrawn.has(up)) { drawnAbove = up; break; }
      }
      if (drawnAbove === null) {
        units.push({ level: keyLevel(id), ids: [id], reveal: uncovers.get(id) ?? 0, held: held.has(id) });
        continue;
      }
      let unit = merges.get(drawnAbove);
      if (!unit) {
        unit = { level: keyLevel(drawnAbove), ids: [], reveal: tileMeshes.has(drawnAbove) ? 1 : 0, held: false };
        merges.set(drawnAbove, unit);
        units.push(unit);
      }
      unit.ids.push(id);
      if (held.has(id)) unit.held = true;
    }
    // Strictly coarsest first: a cover the cap holds back is the first to go
    // next frame, so a stream of small ones behind it cannot starve it.
    const ready = units.filter((u) => !u.held).sort((a, b) => a.level - b.level);
    let released = tileReleasedThisFrame;
    for (let i = 0; i < ready.length; i++) {
      const unit = ready[i]!;
      // `released > 0` exempts the very first unit so a ready cover always
      // goes rather than holding forever on its own size - a unit's reveal is
      // one atomic flip (dropping a retired mesh uncovers every descendant a
      // frame's traversal already meshed under it, not a chosen slice of
      // them), so a unit whose own reveal already exceeds the cap has no
      // smaller release to fall back to. A drawn cut that jumps several
      // levels in one traversal, with no intermediate level ever drawn to
      // leave a kept step behind, can still cross RELEASE_PER_FRAME here.
      if (released > 0 && released + unit.reveal > RELEASE_PER_FRAME) {
        tileReleaseWaiting = ready.length - i;
        break;
      }
      for (const id of unit.ids) dropTileMesh(id);
      released += unit.reveal;
      tileCoverDirty = true;
    }
    if (tileReleaseWaiting > 0) tileCoverDirty = true;
  }

  function keyLevel(id: string): number {
    return Number(id.split('/')[2]);
  }

  /**
   * `--ship --inject builds-unbudgeted`: every mesh built in the frame its own
   * decode landed in.
   *
   * Neither the count nor the slice, so a split's whole replacement set is
   * built and warmed at once. Read off `?buildsunbudgeted=1` as well as
   * through `setTileBuildDebug`, since the first split can be behind a harness
   * reaching into the page.
   */
  let tileBuildUnbudgeted = TEST_SWITCHES && urlFlag('buildsunbudgeted');
  /**
   * `--ship --inject slice-off`: the count alone bounds a frame's builds.
   *
   * The ms slice removed and nothing else, so the deferred total it is read by
   * can be shown reading zero. `?tilesliceoff=1` (DEV only, so no build
   * carries it) or `setTileBuildDebug`.
   */
  let tileBuildSliceOff = TEST_SWITCHES && urlFlag('tilesliceoff');
  /** The recent worst cost of one build, decayed: what the gate predicts the
   *  next one with, since it cannot interrupt one. */
  let tileBuildCostMs = 0;
  /** The longest stretch a frame spent in the build loop and how many meshes
   *  that frame mounted - what the slice bounds, and what a control moves. */
  let tileBuildFrameWorstMs = 0;
  let tileBuildFrameWorstBuilt = 0;
  /** Frames that built at least one mesh, and the meshes they built. */
  let tileBuildFrames = 0;
  let tileBuildTotal = 0;
  /** Frames the SLICE cut short with work still pending, not the count: zero
   *  says the budget never bound and the stretch beside it is about nothing. */
  let tileBuildDeferred = 0;
  /**
   * `--ship --inject tile-reveal`: the pre-fix arrival back.
   *
   * With the warm off, a built mesh's textures go up on the frame the hold
   * releases, which is every replacement of a split at once. Read off
   * `?tilereveal=1` as well as through `setTileWarmDebug`, since the first
   * split can be behind a harness reaching into the page.
   */
  let tileWarmOff = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('tilereveal') === '1';
  /** Meshes warmed since the Atlas opened, so a control can be read back. */
  let tileWarmedTotal = 0;
  /**
   * Tile meshes `updateTiles` built on this frame.
   *
   * What the LUT amortiser yields to: a scattering pass and a mesh build in the
   * same frame are the same budget, and the legs Phase 2 cleared spend theirs
   * on the build.
   */
  let tileMountedThisFrame = 0;
  /**
   * The traversal changed which leaves are drawn on this tick.
   *
   * The focus camera is solved BEFORE `updateTiles`, so a level change under a
   * centre-anchored eye raises the ground it was placed against after the fact
   * - hundreds of metres on Mercury, whose L5 sample is 665 m of ground - and
   * the frame renders with the eye under the terrain. It is re-placed once the
   * traversal has said what it draws.
   */
  let tileDrawChanged = false;

  /**
   * A tile mesh's textures uploaded on the frame it is BUILT, not the frame it
   * is first drawn.
   *
   * A split holds the coarse parent on screen and builds the replacement set
   * hidden behind it (`resolveTileCover`), and three uploads a texture on the
   * first draw that samples it - so the frame the hold released paid for every
   * one of them at once. Measured on the ship arm's REGIONAL->LOCAL leg: 165
   * geometries and 228 textures inside one 65.7 ms render, against a 50 ms
   * budget. Builds are already capped at `TILE_MOUNTS_PER_FRAME`, so charging
   * the upload here spreads it over the frames the build already owns.
   */
  function warmTileMesh(handle: TileMeshHandle): void {
    if (tileWarmOff) return;
    const t0 = performance.now();
    handle.warm(renderer);
    tileWork.warmMs += performance.now() - t0;
    tileWork.warmed++;
    tileWarmedTotal++;
  }

  function dropTileMesh(id: string): void {
    tileHidden.delete(id);
    tileRetired.delete(id);
    const handle = tileMeshes.get(id);
    if (!handle) return;
    tileWork.dropped++;
    tileMeshes.delete(id);
    tileGroup.remove(handle.object);
    tileTriangles -= handle.triangles;
    handle.dispose();
  }

  /**
   * One mesh's own cost, kept as what the next build is predicted to take.
   *
   * The recent worst, decayed, rather than the last or the mean: the gate has
   * to keep a costly leaf out of a frame that cannot hold it, and one such
   * leaf must not hold the gate shut for the rest of the descent.
   */
  function chargeTileBuild(t0: number): void {
    const ms = performance.now() - t0;
    tileBuildCostMs = Math.max(ms, tileBuildCostMs * 0.8);
    tileBuildTotal++;
  }

  function tileReliefField(id: string): TileReliefField | null {
    const sampler = tileReliefFor(tileBody);
    if (sampler === null) return null;
    return {
      on: tileReliefOn,
      heightM: (x, y, z, cellM) => sampler.heightM(x, y, z, cellM),
      heights: reliefMainThread ? null : reliefHeights.get(id) ?? null,
    };
  }

  function tileRadiusM(): number {
    return tileSource?.manifest()?.datum_radius_m ?? registryDatumM(tileBody);
  }

  let reliefInFlight = 0;
  /**
   * Whether this tile's build waits on a worker's relief heights, asking for
   * them when fewer than `RELIEF_IN_FLIGHT` are out. The mesh it replaces, or
   * the retired parent over it, goes on covering the ground meanwhile.
   */
  function reliefWaiting(id: string, tile: LoadedTile): boolean {
    if (reliefMainThread || reliefHeights.has(id)) return false;
    const sampler = tileReliefFor(tileBody);
    if (sampler === null) return false;
    const worker = tileWorker();
    if (!worker.live) return false;
    if (reliefAsked.has(id) || reliefInFlight >= RELIEF_IN_FLIGHT) return true;
    reliefAsked.add(id);
    reliefInFlight++;
    const gen = reliefGen;
    worker.reliefHeights({
      maps: sampler.maps, switches: sampler, bounds: tile.bounds, radiusM: tileRadiusM(), quads: TILE_MESH_QUADS,
    }).then((r) => {
      if (gen !== reliefGen || !reliefAsked.has(id)) return;
      reliefHeights.set(id, r.heights);
      reliefWorkerTimes.note(r.ms);
    }, () => {
      if (gen === reliefGen && reliefAsked.has(id)) reliefHeights.set(id, null);
    }).finally(() => {
      reliefInFlight--;
      deps.live.needsRender = true;
    });
    return true;
  }

  /** Keeps the relief workers fed from the whole queue, not only the tiles this frame's slice reaches. */
  function askReliefHeights(): void {
    if (reliefMainThread || tileReliefFor(tileBody) === null) return;
    for (const queue of [tilePending, tileRemesh]) {
      for (const [id, tile] of queue) {
        if (reliefInFlight >= RELIEF_IN_FLIGHT) return;
        reliefWaiting(id, tile);
      }
    }
  }

  /** One drawn tile's mesh, or null when the build threw. Adds nothing to the scene. */
  function buildTileMesh(id: string, tile: LoadedTile): TileMeshHandle | null {
    try {
      const state = drawStateFor(id);
      const relief = tileReliefField(id);
      const handle = createTileMesh(tile, {
        radiusM: tileRadiusM(),
        anisotropy: renderer.capabilities.getMaxAnisotropy(),
        exaggeration: solar.exag,
        textures: tileTextures,
        albedoTransfer: albedoTransferOf(rowById(tileBody)),
        albedo: state?.albedo ?? null,
        height: heightRefFor(id),
        siteAlbedo: siteAlbedoFor(tile),
        orthoTint: tileDebug.orthoTint,
        morph: state?.morph ?? 1,
        skirts: tileDebug.skirts,
        geomorph: tileDebug.geomorph,
        radialOffsetM: tileDebug.injectTiles.has(id) ? tileDebug.injectStepM : 0,
        radialOffsetEdges: tileDebug.injectEdges,
        atmo: deps.atmosphereView.tileAtmoRef(),
        coverage: tileDebug.coverageFill ? tileCoverage.ref : null,
        artificial: state?.artificial ?? null,
        // No fill fetched for this tile or any ancestor of it is no chunk at
        // all, so a body without one draws the program it drew before.
        artificialFill: tileArtificial.ref,
        // `--inject art-globe-fallback-off` is the control: it keeps this
        // null so a tile with no streamed fill draws nothing at all, which is
        // the pre-fix behaviour the control has to fail against.
        globeArtificial: artificialDebug.legacyTileFill || tileBody === null
          ? null : deps.bodyScene.state.orrery?.byId(tileBody)?.artificial ?? null,
        // The globe's relief carried down onto the streamed ground. Null on
        // every body whose row publishes none, which is every body but one:
        // `--inject relief-globe-only` is the control that puts the rest of
        // them back where they were.
        globeRelief: artificialDebug.reliefGlobeOnly || tileBody === null
          ? null : deps.bodyScene.state.orrery?.byId(tileBody)?.relief ?? null,
        tileRelief: relief,
        bodyShadow: tileBody === null ? null : deps.bodyScene.state.orrery?.byId(tileBody)?.shadow ?? null,
      });
      if (relief !== null) reliefEvalTimes.note(handle.reliefEvalMs);
      tileWork.built++;
      // The decode baked against the set as it stood THEN, and a mesh is built
      // up to a few frames later off the per-frame budget.
      if (!deps.globeMaterial.claimStaleDebug) handle.retintClaims(claimedCellsAt);
      return handle;
    } catch (err: unknown) {
      tileError = `${id}: ${err instanceof Error ? err.message : String(err)}`;
      return null;
    }
  }

  return {
    tileHeightAt,
    tileMeshHeightAt,
    tileHeightProbe,
    tileAlbedoAt,
    groundRadiusUnder,
    drawnGroundHit,
    updateGroundGlide,
    spendGroundGlide,
    get groundGlideM() { return groundGlideM; },
    coverProbe,
    enclosedKeyed,
    resolveTileCover,
    dropTileMesh,
    tileWorker,
    disposeTileWorker,
    get tileBuildUnbudgeted() { return tileBuildUnbudgeted; },
    set tileBuildUnbudgeted(v) { tileBuildUnbudgeted = v; },
    get tileBuildSliceOff() { return tileBuildSliceOff; },
    set tileBuildSliceOff(v) { tileBuildSliceOff = v; },
    get tileReleaseFrameWorst() { return tileReleaseFrameWorst; },
    get tileReleaseWaiting() { return tileReleaseWaiting; },
    get tileBuildCostMs() { return tileBuildCostMs; },
    get tileBuildFrameWorstMs() { return tileBuildFrameWorstMs; },
    get tileBuildFrameWorstBuilt() { return tileBuildFrameWorstBuilt; },
    get tileBuildFrames() { return tileBuildFrames; },
    get tileBuildTotal() { return tileBuildTotal; },
    get tileBuildDeferred() { return tileBuildDeferred; },
    get tileWarmOff() { return tileWarmOff; },
    set tileWarmOff(v) { tileWarmOff = v; },
    get tileWarmedTotal() { return tileWarmedTotal; },
    get tileMountedThisFrame() { return tileMountedThisFrame; },
    get tileDrawChanged() { return tileDrawChanged; },
    TILE_BUILD_SLICE_MS,
    TILE_MOUNTS_PER_FRAME,
    RELEASE_PER_FRAME,
    tileStreamAltM,
    get tileHeightVersion() { return tileHeightVersion; },
    set tileHeightVersion(v) { tileHeightVersion = v; },
    get tileSource() { return tileSource; },
    get tileTree() { return tileTree; },
    get tileMaxLevel() { return tileMaxLevel; },
    get tileGlobalFloor() { return tileGlobalFloor; },
    get tileError() { return tileError; },
    get tileTriangles() { return tileTriangles; },
    get globeUnderlay() { return globeUnderlay; },
    set globeUnderlay(v) { globeUnderlay = v; },
    get underlayMesh() { return underlayMesh; },
    set underlayMesh(v) { underlayMesh = v; },
    globeDepthPushM,
    injectGlobeDepthPush,
    occlusionDebug,
    get tileBody() { return tileBody; },
    get farFieldHeld() { return farFieldHeld; },
    tileCoverage,
    tileArtificial,
    artificialLayerOn,
    tileReliefOn,
    get reliefEvalP95Ms() { return reliefEvalTimes.p95(); },
    get reliefEvalSamples() { return reliefEvalTimes.samples; },
    get reliefEvalBuilds() { return reliefEvalTimes.total; },
    get reliefWorkerP95Ms() { return reliefWorkerTimes.p95(); },
    get reliefWorkerSamples() { return reliefWorkerTimes.samples; },
    get reliefWorkerEvals() { return reliefWorkerTimes.total; },
    dropReliefHeights,
    get reliefMainThread() { return reliefMainThread; },
    set reliefMainThread(v) { reliefMainThread = v; },
    get unmeasuredFill() { return unmeasuredFill; },
    set unmeasuredFill(v) { unmeasuredFill = v; },
    tileMeshes,
    tileHeld,
    tilePending,
    tileRemesh,
    get tileRemeshAll() { return tileRemeshAll; },
    set tileRemeshAll(v) { tileRemeshAll = v; },
    get tileDraw() { return tileDraw; },
    tileDrawn,
    tileRetired,
    tileHidden,
    get creditMixKey() { return creditMixKey; },
    set creditMixKey(v) { creditMixKey = v; },
    get creditSourceIds() { return creditSourceIds; },
    get creditText() { return creditText; },
    get creditForceHidden() { return creditForceHidden; },
    set creditForceHidden(v) { creditForceHidden = v; },
    get creditForceBlank() { return creditForceBlank; },
    set creditForceBlank(v) { creditForceBlank = v; },
    tileTextures,
    get siteAlbedo() { return siteAlbedo; },
    tileDebug,
    tileWork,
    tileWorst,
    get tileHeightMounts() { return tileHeightMounts; },
    get lastTickMs() { return lastTickMs; },
    set lastTickMs(v) { lastTickMs = v; },
    get lastRenderMs() { return lastRenderMs; },
    set lastRenderMs(v) { lastRenderMs = v; },
    syncCruiseGround,
    tilesActive,
    restoreUnderlay,
    applyBodyDrawOrder,
    releaseTiles,
    setArtificialLayer,
    tileView,
    siteLeafKeys,
    applyCreditVisibility,
    updateCredit,
    updateTiles,
    remeshTiles,
  };
}
